import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useFormProgress } from '../../../hooks/useFormProgress';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaUpload } from 'react-icons/fa';
import { fetchApplicationDetails, uploadFormFile, saveFormData } from '../../../utils/apiUtils';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import FilePreviewModal from '../../common/FilePreviewModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';
import ApplicationIdDisplay from '../../common/ApplicationIdDisplay';

// API URL configuration
const API_URL_CONFIG = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL_CONFIG.endsWith('/api') ? API_URL_CONFIG.slice(0, -4) : API_URL_CONFIG;

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// Helper function to extract the filename from a File object or string path
const getFileName = (file: File | string | null): string => {
  if (!file) return '';
  
  if (typeof file === 'string') {
    // For string paths, extract just the filename from the path
    const parts = file.split('/');
    return parts[parts.length - 1] || file;
  } else {
    // For File objects, return the name property
    return file.name || 'Unknown file';
  }
};


// A4 Dimensions (approximate for screen, maintaining aspect ratio)
// 210mm / 297mm = 0.707
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI

const FileUploadProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e9ecef;
  border-radius: 3px;
  margin-top: 5px;
  overflow: hidden;
`;

const FileUploadProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

// Spin animation for upload spinner
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Fade and slide animation for connected field appearance
const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Shake animation for mandatory field validation
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;

// Global style to inject shake keyframes into global CSS for inline style usage
const GlobalShakeStyle = createGlobalStyle`
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
    20%, 40%, 60%, 80% { transform: translateX(8px); }
  }
`;

// Pop animation for thumbnail update
const thumbnailPop = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  70% {
    transform: scale(0.95);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const ThumbnailContainer = styled.div`
  width: 80px;
  height: 80px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  transition: border-color 0.2s, box-shadow 0.2s;
  flex-shrink: 0;
  animation: ${thumbnailPop} 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  
  &:hover {
    border-color: #3498db;
    box-shadow: 0 2px 8px rgba(52,152,219,0.3);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Connected radio option container for Plot Level
const PlotLevelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PlotLevelOptionGroup = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid ${props => props.isSelected ? '#3498db' : '#ddd'};
  background-color: ${props => props.isSelected ? '#ebf5fb' : '#fff'};
  transition: all 0.2s ease;
`;

const PlotLevelOption = styled.label<{ isSelected: boolean; hasValueBox?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  cursor: pointer;
  background-color: ${props => props.isSelected ? '#ebf5fb' : '#fff'};
  font-weight: ${props => props.isSelected ? 600 : 400};
  transition: all 0.2s ease;
  border-right: ${props => props.isSelected && props.hasValueBox ? '1px solid #3498db' : 'none'};
  
  &:hover {
    background-color: ${props => props.isSelected ? '#ebf5fb' : '#f8f9fa'};
  }
  
  input[type="radio"] {
    accent-color: #3498db;
    margin: 0;
  }
`;

const PlotLevelValueBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #ebf5fb;
  animation: ${fadeSlideIn} 0.25s ease-out;
  
  input {
    width: 65px;
    padding: 4px 8px;
    border: 1px solid #85c1e9;
    border-radius: 4px;
    font-size: 0.9em;
    background: #fff;
    
    &:focus {
      outline: none;
      border-color: #3498db;
      box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
    }
  }
  
  span {
    font-weight: 500;
    color: #555;
    font-size: 0.9em;
  }
`;

const UploadSpinner = styled.span`
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  display: inline-block;
  animation: ${spin} 1s linear infinite;
`;

const FileUploadButton = styled.button<{ disabled?: boolean }>`
  background-color: ${props => props.disabled ? '#f0f0f0' : '#6cb288'};
  color: ${props => props.disabled ? '#999999' : 'white'};
  padding: 5px 12px;
  margin-top: 8px;
  border: none;
  border-radius: 4px;
  font-size: 0.9em;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.disabled ? '#f0f0f0' : '#4d9e70'};
  }
`;

const FileUploadStatus = styled.div`
  font-size: 0.85em;
  margin-top: 6px;
  font-weight: 500;
`;

const ClickableFileName = styled.span`
  color: #007bff;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #0056b3;
  }
`;

const FileUploadSuccess = styled.div`
  color: #28a745;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85em;
  margin-top: 6px;
`;

const FileUploadError = styled.div`
  color: #dc3545; /* Red for errors */
  font-size: 0.85em;
  margin-top: 6px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 5px;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 6px;
  
  input[type="radio"] {
    appearance: none;
    -webkit-appearance: none;
    width: 18px;
    height: 18px;
    border: 2px solid #6cb288;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    
    &:checked {
      border-color: #6cb288;
      background-color: #f0f9f4;
      
      &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: 3px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #6cb288;
      }
    }
  }
  
  &:hover input[type="radio"] {
    border-color: #4d9e70;
    box-shadow: 0 0 0 1px rgba(108, 178, 136, 0.2);
  }
`;

const InfoText = styled.p`
  font-size: 0.9em;
  color: #555;
  margin-bottom: 10px;
  line-height: 1.5;
`;

const InlineInputsContainer = styled.div`
  display: flex;
  gap: 10px; /* Adjust gap as needed */
  align-items: center; /* Vertically align items if they have different heights */

  & > * { /* Target direct children, assuming they will be the StyledInput components */
    flex: 1; /* Allow inputs to share space equally */
  }
  margin-bottom: 15px; /* Spacing after the row of inputs */
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2); /* Modern gradient */
  color: white;
  border: none;
  border-radius: 50%; /* Circular button */
  width: 60px; /* Size of the button */
  height: 60px;
  font-size: 22px; /* Slightly smaller icon size for a cleaner look */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000; /* Ensure it's on top */
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &::after {
    content: 'Back';
    position: absolute;
    font-size: 12px;
    bottom: 7px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545; /* Red for offline */
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000; /* Ensure it's above other elements */
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px; /* Use min-height to allow content to expand if needed */
  background-color: white;
  padding: 50px; /* Increased padding for more breathing room */
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07); /* Softer, more modern shadow */
  box-sizing: border-box;
  position: relative; /* For page number positioning */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  color: #212529; /* Darker, more standard text color */
  font-size: 13px; /* Base font size for content */
  line-height: 1.6;

  /* For print, you might want to add @media print styles */
`;

const LogoImage = styled.img`
  height: 75px; /* Increased by 25% from 60px */
  margin-bottom: 10px;
`;

const SubTitle = styled.p`
  font-size: 0.56em; /* 70% of original 0.8em */
  font-style: italic;
  color: #333; /* Darker text */
  text-align: center;
  margin-bottom: 20px;
  font-weight: 500;
`;

const TopSectionLayout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const LeftFields = styled.div`
  flex: 3; /* Takes up more space */
  display: flex;
  flex-direction: column;
`;

const RightPhotoPlaceholder = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column; /* Stack items vertically */
  align-items: center; /* Center items horizontally */
  padding-left: 20px; /* Space between fields and photo box */
`;

const PhotoBox = styled.div<{ disabled?: boolean }>`
  width: 150px; /* As per form image */
  height: 180px; /* As per form image */
  border: 1px solid #ccc;
  display: flex;
  flex-direction: column; 
  justify-content: center;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  background-color: #f9f9f9;
  text-align: center; /* For placeholder text */
  position: relative; /* For positioning the animated image */
  overflow: hidden;  /* To clip the image during animation */
  /* Direct img styling removed as AnimatedPhotoImage handles it. */
`;

const PhotoBoxText = styled.p`
  font-size: 0.8em;
  color: #555;
  margin: 5px 0 0 0;
  padding: 5px;
`;

const AnimatedPhotoImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  clip-path: inset(100% 0 0 0); /* Initial: hidden, revealed from bottom (top edge 100% clipped) */
  transition: clip-path 2s ease-in-out; /* Animation duration 2 seconds */

  &.reveal {
    clip-path: inset(0 0 0 0); /* Final: fully revealed */
  }
`;

const UploadConfirmationText = styled.p`
  font-size: 0.8em;
  color: green;
  margin-top: 8px;
  text-align: center;
  font-weight: bold;
  height: 1.2em; /* Reserve space to prevent layout shift */
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 30px; /* Increased spacing */
  border-bottom: 1px solid #dee2e6; /* Softer border */
  padding-bottom: 20px;
`;



const FormTitle = styled.h1`
  font-size: 1.5em; /* Larger title */
  font-weight: 600;
  text-align: center;
  margin: 0 0 15px 0; /* Adjusted margin */
  text-transform: uppercase;
  letter-spacing: 0.5px; /* Adjusted for a tighter look */
  color: #004085; /* Dark blue for title */
`;



const FormContentContainer = styled.div`
  /* font-size and line-height will now be inherited from A4PageContainer or overridden by specific elements */
`;

const DownloadButton = styled.button`
  padding: 8px 12px;
  margin-left: 10px;
  font-size: 0.9em;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;







const FormFieldRow = styled.div`
  display: flex;
  align-items: baseline; /* Better for aligning text labels with input text */
  margin-bottom: 12px; /* Increased spacing */
`;

const AppNoRowContainer = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 12px;
  position: relative; /* For positioning message if needed */
`;

const GenerateAppNoButton = styled.button`
  padding: 8px 12px;
  margin-left: 10px;
  font-size: 0.9em;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const AppNoMessageText = styled.p`
  font-size: 0.8em;
  color: green;
  margin-top: -8px; /* Adjust to position nicely below the row */
  margin-bottom: 8px;
  min-height: 1.2em; /* Reserve space */
  text-align: left; /* Align with label */
  padding-left: calc( /* Approximate padding to align with input start if label width is known or fixed */
    1em /* Label font-size approx */ * 
    20 /* Avg chars in 'Application No. (for office use):' */ * 
    0.6 /* Avg char width factor */ + 
    8px /* Label margin-right */
  );
`;

const FormFieldFullWidth = styled.div`
  display: flex;
  flex-direction: column; 
  margin-bottom: 12px; /* Increased spacing */
`;

const Label = styled.label`
  margin-right: 8px;
  white-space: nowrap;
  font-weight: 500; /* Semi-bold for better emphasis */
  color: #495057; /* Standard label color */
  font-size: 0.9em; /* Slightly smaller than input text if base is 13px */
  margin-bottom: 4px; /* Space if label is on its own line above input */
`;

const LabelInline = styled.label`
  margin: 0 8px 0 4px;
  white-space: nowrap;
  font-weight: 500;
  color: #495057;
  font-size: 0.9em;
`;

const StyledInput = styled.input`
  flex-grow: 1;
  border: 1px solid #a8d0b9; /* Greenish border */
  border-radius: 4px; /* Rounded corners */
  padding: 8px 12px; /* Comfortable padding */
  font-size: 1em;
  font-family: inherit;
  background-color: #f8fcfa; /* Very subtle green tint */
  color: #495057;
  line-height: 1.5;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #6cb288; /* Stronger green when focused */
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }

  /* Basic styling for date/select to blend better, though limited */
  &[type="date"],
  &[type="datetime-local"],
  &[type="month"],
  &[type="time"] {
    padding-right: 8px; /* Adjust padding for date pickers */
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border: 1px solid #a8d0b9; /* Greenish border */
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 1em;
  font-family: inherit;
  background-color: #f8fcfa; /* Very subtle green tint */
  color: #495057;
  line-height: 1.5;
  resize: vertical; /* Allow vertical resize, disable horizontal */
  min-height: 40px; /* Approx 2 lines of text */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #6cb288; /* Stronger green when focused */
    box-shadow: 0 0 0 0.2rem rgba(108, 178, 136, 0.25);
  }

  &:hover {
    border-color: #8ac0a0;
  }

  &::placeholder {
    color: #7ea38c;
    opacity: 1; 
  }
`;



const InstructionText = styled.p`
  font-size: 9px;
  font-style: italic;
  color: #555;
  margin: 10px 0;
  text-align: left;
`;

const SectionTitle = styled.h2`
  font-size: 1.25em; /* Relative to base font size */
  font-weight: 600;
  color: #0056b3; /* A darker primary blue */
  margin-top: 35px;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
`;

const IndividualFields = styled.div`
  /* Styles for the individual fields section if needed */
`;

// Styling for Radio Buttons
const RadioGroupContainer = styled.div`
  display: flex;
  flex-wrap: wrap; /* Allow wrapping if too many options */
  align-items: center;
  margin-top: 5px; /* Space above radio group */
  margin-bottom: 5px; /* Space below radio group */
`;

const RadioOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 20px; /* Space between radio options */
  margin-bottom: 5px; /* Space for wrapped items */

  input[type="radio"] {
    margin-right: 6px;
    width: 16px; /* Custom size */
    height: 16px; /* Custom size */
    accent-color: #007bff; /* Modern way to color radio/checkbox */
    cursor: pointer;
  }

  label {
    font-weight: normal; /* Normal weight for radio labels */
    font-size: 1em;
    color: #495057;
    cursor: pointer;
  }
`;

// Styling for Select (Dropdown)
const StyledSelect = styled.select`
  flex-grow: 1;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 1em;
  font-family: inherit;
  background-color: #fff;
  color: #495057;
  line-height: 1.5;
  appearance: none; /* Remove default system appearance */
  background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20fill%3D%22%236c757d%22%20d%3D%22M7.41%208.59L12%2013.17l4.59-4.58L18%2010l-6%206-6-6%201.41-1.41z%22%2F%3E%3C%2Fsvg%3E');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px 16px;
  padding-right: 36px; /* Make space for the arrow */
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: #80bdff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  option {
    font-weight: normal;
  }
`;


const SaveDraftButton = styled.button`
  background-color: #ffc107; /* Amber color for draft */
  color: #212529; /* Dark text for contrast */
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px; /* Space from submit button or other elements */
  margin-right: 10px; /* Space from submit button if on same line */
  display: inline-block; /* Allow side-by-side with submit if desired */
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800; /* Darker amber on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px; /* Slightly larger radius */
  font-size: 1em; /* Relative to base */
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px; /* More space above */
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9; /* Darker shade on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px); /* Slight press effect */
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const DeclarationText = styled.p`
  font-size: 0.9em;
  margin-top: 25px;
  margin-bottom: 15px;
  line-height: 1.5;
  text-align: justify;
`;

const SignatureSection = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
  align-items: flex-end; /* Align items to the bottom for varying heights */
`;

const SignatureField = styled.div`
  flex-basis: 45%; // Adjust as needed
  display: flex;
  flex-direction: column;

  span { // For the "Date:" and "Signature of Applicant:" text
    margin-bottom: 8px;
    font-weight: 500;
    color: #495057;
    font-size: 0.9em;
  }

  .signature-line {
    border-bottom: 1px solid #495057;
    height: 30px; // Space for signature
    /* User will sign here, so it's not an input */
  }
`;


const TopRightButtonContainer = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 10;
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

const CenteredButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 15px; 
`;

const RowButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 20px;
`;

// Interface for partner/director details (for LLP, Pvt Ltd, Public Ltd, etc.)
interface PartnerDirectorDetail {
  id: string;
  nameInBlocks: string;
  fatherOrHusbandName: string;
  dateOfBirth: string;
  panCardFile?: File | string | null;
  dobProofFile?: File | string | null;
  isMinimumEducationMet: string;
  qualification: string;
  qualificationProofFile?: File | string | null;
  currentAddress: string;
  permanentAddress: string;
  permanentAddressSameAsCurrent: boolean;
  addressProofFile?: File | string | null;
  personalMobile: string;
  secondaryMobile: string;
  emailId: string;
  nationality: string;
  gender: string;
  maritalStatus: string;
}

interface FormData {
  applicationNoOfficeUse: string;
  location: string;
  locationFrom: string;
  locationTo: string;
  locationMinPlotFrontage: string;
  locationMinPlotDepth: string;
  advertisedOn: string; // Date
  advertisedIn: string; // Newspaper/Medium
  advertisementSource: string; // New field for how they found out about the ad
  dealershipType: string;
  numberOfPartners: number;
  partnerDirectorDetails: PartnerDirectorDetail[];
  nameInBlocks: string;
  fatherOrHusbandName: string;
  dateOfBirth: string;
  dobProofFile?: File | string | null;
  qualification: string;
  qualificationProofFile?: File | string | null;
  currentAddress: string;
  permanentAddress: string;
  permanentAddressSameAsCurrent: boolean;
  addressProofFile?: File | string | null;
  personalMobile: string;
  secondaryMobile: string;
  emailId: string;
  nationality: string;
  gender: string;
  maritalStatus: string;
  errors?: {
    personalMobile?: string;
    secondaryMobile?: string;
    emailId?: string;
    landLocationCoordinates?: string;
  };
  
  // Identification & Statutory Details
  aadhaarNumber: string;
  aadhaarVerified: string;
  panNumberStatutory: string;
  panVerified: string;
  gstNumber: string;
  gstVerified: string;
  identityProofVoterId: boolean;
  identityProofPassport: boolean;
  identityProofDrivingLicense: boolean;
  identityDocumentNumber: string;
  
  // Educational Qualification
  eduSscBoard: string;
  eduSscMarksObtained: string;
  eduSscOutOfMarks: string;
  eduSscPercentage: string;
  eduSscYearOfPassing: string;
  eduHscBoard: string;
  eduHscMarksObtained: string;
  eduHscOutOfMarks: string;
  eduHscPercentage: string;
  eduHscYearOfPassing: string;
  eduGraduateBoard: string;
  eduGraduateMarksObtained: string;
  eduGraduateOutOfMarks: string;
  eduGraduatePercentage: string;
  eduGraduateYearOfPassing: string;
  eduOthersBoard: string;
  eduOthersMarksObtained: string;
  eduOthersOutOfMarks: string;
  eduOthersPercentage: string;
  eduOthersYearOfPassing: string;
  
  // Non Individual Applicant Entity fields
  niTypeOfApplicant: string;
  niNameOfEntity: string;
  niDateOfRegistrationIncorporation: string;
  niCompanyRegisteredInIndia: string;
  niNameOfAuthorizedSignatory: string;
  niMobilePrimary: string;
  niMobileAlternate: string;
  niPhoneResidential: string;
  niPhoneOfficial: string;
  niFax: string;
  niEmailId: string;
  niFinancialYear1: string;
  niProfitAfterTax1: string;
  niLoss1: string;
  niFinancialYear2: string;
  niProfitAfterTax2: string;
  niLoss2: string;
  niFinancialYear3: string;
  niProfitAfterTax3: string;
  niLoss3: string;
  niCurrentFlatHouseNo: string;
  niCurrentBuildingName: string;
  niCurrentPlotNo: string;
  niCurrentStreetName: string;
  niCurrentLocality: string;
  niCurrentLandmark: string;
  niCurrentCityTown: string;
  niCurrentDistrict: string;
  niCurrentState: string;
  niCurrentPinCode: string;
  niCurrentMobilePrimary: string;
  niCurrentMobileAlternate: string;
  niCurrentPhoneResidential: string;
  niCurrentPhoneOfficial: string;
  niCurrentFax: string;
  niCurrentEmailId: string;
  niPermanentAddressSameAsCurrent: boolean;
  niPermanentFlatHouseNo: string;
  niPermanentBuildingName: string;
  niPermanentPlotNo: string;
  niPermanentStreetName: string;
  niPermanentLocality: string;
  niPermanentLandmark: string;
  niPermanentCityTown: string;
  niPermanentDistrict: string;
  niPermanentState: string;
  niPermanentPinCode: string;
  niPermanentMobilePrimary: string;
  niPermanentMobileAlternate: string;
  niPermanentPhoneResidential: string;
  
  // BUSINESS / EMPLOYMENT DETAILS
  presentOccupation: string;
  businessEmploymentDetails: string;
  totalWorkExperienceYears: string;
  relevantIndustryExperience: string;
  
  // FINANCIAL CAPABILITY
  annualIncome: string;
  netWorthAtApplication: string;
  sourceOfInvestment: string;
  abilityToMeetRequiredInvestment: string;
  
  // FINANCIAL CAPABILITY DOCUMENT CHECKLIST
  finDocProjectReport: string;
  finDocProjectReportFile?: File | string | null;
  finDocBankStatement: string;
  finDocBankStatementFile?: File | string | null;
  finDocFdTdReceipts: string;
  finDocFdTdReceiptsFile?: File | string | null;
  finDocInvestmentCertificates: string;
  finDocInvestmentCertificatesFile?: File | string | null;
  finDocMutualFundStatement: string;
  finDocMutualFundStatementFile?: File | string | null;
  finDocDematStatement: string;
  finDocDematStatementFile?: File | string | null;
  finDocCaCertificate: string;
  finDocCaCertificateFile?: File | string | null;
  finDocDpCertificate: string;
  finDocDpCertificateFile?: File | string | null;
  finDocAttestedFinancials: string;
  finDocAttestedFinancialsFile?: File | string | null;
  finDocNotarizedAffidavit: string;
  finDocNotarizedAffidavitFile?: File | string | null;
  finDocBankConfirmationLetter: string;
  finDocBankConfirmationLetterFile?: File | string | null;
  finDocPropertyDocuments: string;
  finDocPropertyDocumentsFile?: File | string | null;
  finDocAssetDocuments: string;
  finDocAssetDocumentsFile?: File | string | null;
  finDocIncomeProof: string;
  finDocIncomeProofFile?: File | string | null;
  
  // LAND DETAILS (MANDATORY)
  landOwnershipStatus: string;
  periodOfLeaseYears: string;
  periodOfLeaseMonths: string;
  periodOfLeaseDays: string;
  landRegistrationDate: string;
  landCompleteAddress: string;
  landLocationCoordinates: string;
  landPlotDrawingFile?: File | string | null;
  landSurveyNo: string;
  landGataNo: string;
  landKhataNo: string;
  landKhatauniNo: string;
  landFerfarNo: string;
  landVargaNo: string;
  landAreaSqMtrs: string;
  landFrontageMtrs: string;
  landDepthMtrs: string;
  landApproachRoadWidthMtrs: string;
  landPlotLevelType: string;
  landPlotLevelMtr: string;
  landPlotLevelAboveMtr: string;
  landPlotLevelBelowMtr: string;
  // Land To be offered to MNGL
  mnglLandAreaSqMtrs: string;
  mnglLandFrontageMtrs: string;
  mnglLandDepthMtrs: string;
  mnglTotalAreaSqMtrs: string;
  // Detail of surroundings
  surroundingsEast: string;
  surroundingsWest: string;
  surroundingsNorth: string;
  surroundingsSouth: string;
  landUse: string;
  landConvertedFromAgriculture: string;
  landConvertedFromAgricultureDetails: string;
  landTitleClear: string;
  landTitleClearDetails: string;
  landLitigationFree: string;
  landLitigationFreeDetails: string;
  landPowerConnectionAvailable: string;
  landWaterConnectionAvailable: string;
  landEncroachment: string;
  landEncroachmentDetails: string;
  landGovtAcquisitionPlan: string;
  landGovtAcquisitionPlanDetails: string;
  // OTHER INFORMATION
  nearestFuelStationAvailable: string;
  nearestFuelStationDistance: string;
  nearestPoliceStationName: string;
  nearestFireStationName: string;
  highVoltageTransmissionLine: string;
  highVoltageTransmissionLineDistance: string;
  landAbuttingRoadBoundary: string;
  landAbuttingRoadBoundaryDistance: string;
}

interface ApplicationFormPage2Props {
  formData?: any;
  isViewMode?: boolean;
}

const ApplicationFormPage2: React.FC<ApplicationFormPage2Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [applicationId, setApplicationId] = useState<string>('');
  const [photo, setPhoto] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  const [isRevealing, setIsRevealing] = useState<boolean>(false);
  const [photoUploadMessage, setPhotoUploadMessage] = useState<string>('');

  // Refs for file inputs on this page
  const landPlotDrawingFileInputRef = useRef<HTMLInputElement>(null);

  // Consolidated file upload states
  type UploadableField = 'landPlotDrawingFile' | 'finDocProjectReportFile' | 'finDocBankStatementFile' | 'finDocFdTdReceiptsFile' | 'finDocInvestmentCertificatesFile' | 'finDocMutualFundStatementFile' | 'finDocDematStatementFile' | 'finDocCaCertificateFile' | 'finDocDpCertificateFile' | 'finDocAttestedFinancialsFile' | 'finDocNotarizedAffidavitFile' | 'finDocBankConfirmationLetterFile' | 'finDocPropertyDocumentsFile' | 'finDocAssetDocumentsFile' | 'finDocIncomeProofFile';
  const [uploadProgress, setUploadProgress] = useState<Partial<Record<UploadableField, number>>>({});
  const [uploadStatus, setUploadStatus] = useState<Partial<Record<UploadableField, 'idle' | 'uploading' | 'success' | 'error'>>>({});
  const [uploadMessage, setUploadMessage] = useState<Partial<Record<UploadableField, string>>>({});
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Lease modal state
  const [showLeaseModal, setShowLeaseModal] = useState<boolean>(false);
  const [leaseAgreed, setLeaseAgreed] = useState<boolean>(false);
  
  // Upload error modal state
  const [uploadErrorModal, setUploadErrorModal] = useState<{ show: boolean; message: string; fieldName: string }>({ show: false, message: '', fieldName: '' });
  
  // Coordinates help modal state
  const [showCoordinatesHelpModal, setShowCoordinatesHelpModal] = useState<boolean>(false);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Application lock status (locked after payment or by admin)
  const [isApplicationLocked, setIsApplicationLocked] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(propFormData || {
    // Initialize with the new applicationId if available
    applicationNoOfficeUse: '', // This will be set in useEffect
    location: '',
    locationFrom: '',
    locationTo: '',
    locationMinPlotFrontage: '',
    locationMinPlotDepth: '',
    advertisedOn: '',
    advertisedIn: '',
    advertisementSource: '',
    dealershipType: '',
    numberOfPartners: 0,
    partnerDirectorDetails: [],
    nameInBlocks: '',
    fatherOrHusbandName: '',
    dateOfBirth: '',
    dobProofFile: null,
    qualification: '',
    qualificationProofFile: null,
    currentAddress: '',
    permanentAddress: '',
    permanentAddressSameAsCurrent: false,
    addressProofFile: null,
    personalMobile: '',
    secondaryMobile: '',
    emailId: '',
    nationality: '',
    gender: '',
    maritalStatus: '',
    errors: {},
    // Identification & Statutory Details
    aadhaarNumber: '',
    aadhaarVerified: '',
    panNumberStatutory: '',
    panVerified: '',
    gstNumber: '',
    gstVerified: '',
    identityProofVoterId: false,
    identityProofPassport: false,
    identityProofDrivingLicense: false,
    identityDocumentNumber: '',
    // Educational Qualification
    eduSscBoard: '',
    eduSscMarksObtained: '',
    eduSscOutOfMarks: '',
    eduSscPercentage: '',
    eduSscYearOfPassing: '',
    eduHscBoard: '',
    eduHscMarksObtained: '',
    eduHscOutOfMarks: '',
    eduHscPercentage: '',
    eduHscYearOfPassing: '',
    eduGraduateBoard: '',
    eduGraduateMarksObtained: '',
    eduGraduateOutOfMarks: '',
    eduGraduatePercentage: '',
    eduGraduateYearOfPassing: '',
    eduOthersBoard: '',
    eduOthersMarksObtained: '',
    eduOthersOutOfMarks: '',
    eduOthersPercentage: '',
    eduOthersYearOfPassing: '',
    // Non Individual Applicant Entity fields
    niTypeOfApplicant: '',
    niNameOfEntity: '',
    niDateOfRegistrationIncorporation: '',
    niCompanyRegisteredInIndia: '',
    niNameOfAuthorizedSignatory: '',
    niMobilePrimary: '',
    niMobileAlternate: '',
    niPhoneResidential: '',
    niPhoneOfficial: '',
    niFax: '',
    niEmailId: '',
    niFinancialYear1: '',
    niProfitAfterTax1: '',
    niLoss1: '',
    niFinancialYear2: '',
    niProfitAfterTax2: '',
    niLoss2: '',
    niFinancialYear3: '',
    niProfitAfterTax3: '',
    niLoss3: '',
    niCurrentFlatHouseNo: '',
    niCurrentBuildingName: '',
    niCurrentPlotNo: '',
    niCurrentStreetName: '',
    niCurrentLocality: '',
    niCurrentLandmark: '',
    niCurrentCityTown: '',
    niCurrentDistrict: '',
    niCurrentState: '',
    niCurrentPinCode: '',
    niCurrentMobilePrimary: '',
    niCurrentMobileAlternate: '',
    niCurrentPhoneResidential: '',
    niCurrentPhoneOfficial: '',
    niCurrentFax: '',
    niCurrentEmailId: '',
    niPermanentAddressSameAsCurrent: false,
    niPermanentFlatHouseNo: '',
    niPermanentBuildingName: '',
    niPermanentPlotNo: '',
    niPermanentStreetName: '',
    niPermanentLocality: '',
    niPermanentLandmark: '',
    niPermanentCityTown: '',
    niPermanentDistrict: '',
    niPermanentState: '',
    niPermanentPinCode: '',
    niPermanentMobilePrimary: '',
    niPermanentMobileAlternate: '',
    niPermanentPhoneResidential: '',
    // BUSINESS / EMPLOYMENT DETAILS
    presentOccupation: '',
    businessEmploymentDetails: '',
    totalWorkExperienceYears: '',
    relevantIndustryExperience: '',
    // FINANCIAL CAPABILITY
    annualIncome: '',
    netWorthAtApplication: '',
    sourceOfInvestment: '',
    abilityToMeetRequiredInvestment: '',
    // FINANCIAL CAPABILITY DOCUMENT CHECKLIST
    finDocProjectReport: '',
    finDocProjectReportFile: null,
    finDocBankStatement: '',
    finDocBankStatementFile: null,
    finDocFdTdReceipts: '',
    finDocFdTdReceiptsFile: null,
    finDocInvestmentCertificates: '',
    finDocInvestmentCertificatesFile: null,
    finDocMutualFundStatement: '',
    finDocMutualFundStatementFile: null,
    finDocDematStatement: '',
    finDocDematStatementFile: null,
    finDocCaCertificate: '',
    finDocCaCertificateFile: null,
    finDocDpCertificate: '',
    finDocDpCertificateFile: null,
    finDocAttestedFinancials: '',
    finDocAttestedFinancialsFile: null,
    finDocNotarizedAffidavit: '',
    finDocNotarizedAffidavitFile: null,
    finDocBankConfirmationLetter: '',
    finDocBankConfirmationLetterFile: null,
    finDocPropertyDocuments: '',
    finDocPropertyDocumentsFile: null,
    finDocAssetDocuments: '',
    finDocAssetDocumentsFile: null,
    finDocIncomeProof: '',
    finDocIncomeProofFile: null,
    // LAND DETAILS (MANDATORY)
    landOwnershipStatus: '',
    periodOfLeaseYears: '',
    periodOfLeaseMonths: '',
    periodOfLeaseDays: '',
    landRegistrationDate: '',
    landCompleteAddress: '',
    landLocationCoordinates: '',
    landPlotDrawingFile: null,
    landSurveyNo: '',
    landGataNo: '',
    landKhataNo: '',
    landKhatauniNo: '',
    landFerfarNo: '',
    landVargaNo: '',
    landAreaSqMtrs: '',
    landFrontageMtrs: '',
    landDepthMtrs: '',
    landApproachRoadWidthMtrs: '',
    landPlotLevelType: '',
    landPlotLevelMtr: '',
    landPlotLevelAboveMtr: '',
    landPlotLevelBelowMtr: '',
    // Land To be offered to MNGL
    mnglLandAreaSqMtrs: '',
    mnglLandFrontageMtrs: '',
    mnglLandDepthMtrs: '',
    mnglTotalAreaSqMtrs: '',
    // Detail of surroundings
    surroundingsEast: '',
    surroundingsWest: '',
    surroundingsNorth: '',
    surroundingsSouth: '',
    landUse: '',
    landConvertedFromAgriculture: '',
    landConvertedFromAgricultureDetails: '',
    landTitleClear: '',
    landTitleClearDetails: '',
    landLitigationFree: '',
    landLitigationFreeDetails: '',
    landPowerConnectionAvailable: '',
    landWaterConnectionAvailable: '',
    landEncroachment: '',
    landEncroachmentDetails: '',
    landGovtAcquisitionPlan: '',
    landGovtAcquisitionPlanDetails: '',
    // OTHER INFORMATION
    nearestFuelStationAvailable: '',
    nearestFuelStationDistance: '',
    nearestPoliceStationName: '',
    nearestFireStationName: '',
    highVoltageTransmissionLine: '',
    highVoltageTransmissionLineDistance: '',
    landAbuttingRoadBoundary: '',
    landAbuttingRoadBoundaryDistance: ''
  });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [showPlotSizeModal, setShowPlotSizeModal] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [showBrochureModal, setShowBrochureModal] = useState<boolean>(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  const [modalFieldName, setModalFieldName] = useState<string | null>(null);
  
  // Save timeout reference for debouncing
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);

  // Mandatory fields state from backend settings
  const [mandatoryFieldsPage2, setMandatoryFieldsPage2] = useState<Record<string, boolean>>({
    landOwnershipStatus: true,
    periodOfLease: true,
    landCompleteAddress: true,
    landLocationCoordinates: true,
    landPlotDrawingFile: true,
    landSurveyNo: true,
    landKhataNo: true,
    landKhatauniNo: true,
    landFerfarNo: true,
    landVargaNo: true,
    mnglLandFrontageMtrs: true,
    mnglLandDepthMtrs: true,
    mnglTotalAreaSqMtrs: true,
    landApproachRoadWidthMtrs: true,
    landPlotLevelType: true,
    surroundingsEast: true,
    surroundingsWest: true,
    surroundingsNorth: true,
    surroundingsSouth: true,
    landUse: true,
    landTitleClear: true,
    landLitigationFree: true,
    landPowerConnectionAvailable: true,
    landWaterConnectionAvailable: true,
    landEncroachment: true,
    landGovtAcquisitionPlan: true,
    nearestPoliceStationName: true,
    nearestFireStationName: true,
    highVoltageTransmissionLine: true,
    landAbuttingRoadBoundary: true,
  });

  // Refs for mandatory fields (for scrolling to first invalid field)
  const landOwnershipStatusRef = useRef<HTMLTableRowElement>(null);
  const periodOfLeaseRef = useRef<HTMLTableRowElement>(null);
  const landCompleteAddressRef = useRef<HTMLTableRowElement>(null);
  const landLocationCoordinatesRef = useRef<HTMLTableRowElement>(null);
  const landPlotDrawingFileRef = useRef<HTMLTableRowElement>(null);
  const landSurveyNoRef = useRef<HTMLTableRowElement>(null);
  const landKhataNoRef = useRef<HTMLTableRowElement>(null);
  const landKhatauniNoRef = useRef<HTMLTableRowElement>(null);
  const landFerfarNoRef = useRef<HTMLTableRowElement>(null);
  const landVargaNoRef = useRef<HTMLTableRowElement>(null);
  const mnglLandFrontageMtrsRef = useRef<HTMLTableRowElement>(null);
  const mnglLandDepthMtrsRef = useRef<HTMLTableRowElement>(null);
  const mnglTotalAreaSqMtrsRef = useRef<HTMLTableRowElement>(null);
  const landApproachRoadWidthMtrsRef = useRef<HTMLTableRowElement>(null);
  const landPlotLevelTypeRef = useRef<HTMLTableRowElement>(null);
  const surroundingsEastRef = useRef<HTMLTableRowElement>(null);
  const surroundingsWestRef = useRef<HTMLTableRowElement>(null);
  const surroundingsNorthRef = useRef<HTMLTableRowElement>(null);
  const surroundingsSouthRef = useRef<HTMLTableRowElement>(null);
  const landUseRef = useRef<HTMLTableRowElement>(null);
  const landTitleClearRef = useRef<HTMLTableRowElement>(null);
  const landLitigationFreeRef = useRef<HTMLTableRowElement>(null);
  const landPowerConnectionAvailableRef = useRef<HTMLTableRowElement>(null);
  const landWaterConnectionAvailableRef = useRef<HTMLTableRowElement>(null);
  const landEncroachmentRef = useRef<HTMLTableRowElement>(null);
  const landGovtAcquisitionPlanRef = useRef<HTMLTableRowElement>(null);
  const nearestPoliceStationNameRef = useRef<HTMLTableRowElement>(null);
  const nearestFireStationNameRef = useRef<HTMLTableRowElement>(null);
  const highVoltageTransmissionLineRef = useRef<HTMLTableRowElement>(null);
  const landAbuttingRoadBoundaryRef = useRef<HTMLTableRowElement>(null);

  // Shake state for mandatory fields
  const [isLandOwnershipStatusShaking, setIsLandOwnershipStatusShaking] = useState(false);
  const [isPeriodOfLeaseShaking, setIsPeriodOfLeaseShaking] = useState(false);
  const [isLandCompleteAddressShaking, setIsLandCompleteAddressShaking] = useState(false);
  const [isLandLocationCoordinatesShaking, setIsLandLocationCoordinatesShaking] = useState(false);
  const [isLandPlotDrawingFileShaking, setIsLandPlotDrawingFileShaking] = useState(false);
  const [isLandSurveyNoShaking, setIsLandSurveyNoShaking] = useState(false);
  const [isLandKhataNoShaking, setIsLandKhataNoShaking] = useState(false);
  const [isLandKhatauniNoShaking, setIsLandKhatauniNoShaking] = useState(false);
  const [isLandFerfarNoShaking, setIsLandFerfarNoShaking] = useState(false);
  const [isLandVargaNoShaking, setIsLandVargaNoShaking] = useState(false);
  const [isMnglLandFrontageMtrsShaking, setIsMnglLandFrontageMtrsShaking] = useState(false);
  const [isMnglLandDepthMtrsShaking, setIsMnglLandDepthMtrsShaking] = useState(false);
  const [isMnglTotalAreaSqMtrsShaking, setIsMnglTotalAreaSqMtrsShaking] = useState(false);
  const [isLandApproachRoadWidthMtrsShaking, setIsLandApproachRoadWidthMtrsShaking] = useState(false);
  const [isLandPlotLevelTypeShaking, setIsLandPlotLevelTypeShaking] = useState(false);
  const [isSurroundingsEastShaking, setIsSurroundingsEastShaking] = useState(false);
  const [isSurroundingsWestShaking, setIsSurroundingsWestShaking] = useState(false);
  const [isSurroundingsNorthShaking, setIsSurroundingsNorthShaking] = useState(false);
  const [isSurroundingsSouthShaking, setIsSurroundingsSouthShaking] = useState(false);
  const [isLandUseShaking, setIsLandUseShaking] = useState(false);
  const [isLandTitleClearShaking, setIsLandTitleClearShaking] = useState(false);
  const [isLandLitigationFreeShaking, setIsLandLitigationFreeShaking] = useState(false);
  const [isLandPowerConnectionAvailableShaking, setIsLandPowerConnectionAvailableShaking] = useState(false);
  const [isLandWaterConnectionAvailableShaking, setIsLandWaterConnectionAvailableShaking] = useState(false);
  const [isLandEncroachmentShaking, setIsLandEncroachmentShaking] = useState(false);
  const [isLandGovtAcquisitionPlanShaking, setIsLandGovtAcquisitionPlanShaking] = useState(false);
  const [isNearestPoliceStationNameShaking, setIsNearestPoliceStationNameShaking] = useState(false);
  const [isNearestFireStationNameShaking, setIsNearestFireStationNameShaking] = useState(false);
  const [isHighVoltageTransmissionLineShaking, setIsHighVoltageTransmissionLineShaking] = useState(false);
  const [isLandAbuttingRoadBoundaryShaking, setIsLandAbuttingRoadBoundaryShaking] = useState(false);

  // Global progress bar (across Page1+2+3+4) — PageA and Page5 are info-only
  const formProgress = useFormProgress(formData);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    // If formData is passed as a prop, we don't need to generate or fetch an ID
    if (propFormData) {
      if (propFormData.applicationNoOfficeUse) {
        setApplicationId(propFormData.applicationNoOfficeUse);
      }
      return;
    }

    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[AppFormPage2 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      // TODO: If loading an existing draft, consider pre-filling formData by fetching from backend
      // For now, just ensure formData has the ID if it's different
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[AppFormPage2 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true; // Mark as newly generated for initial auto-save
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, propFormData]); // Only re-run if location.state or propFormData changes

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    const loadDraftData = async () => {
      // If formData is passed as a prop, don't fetch from API
      if (propFormData) {
        console.log('[AppFormPage2 LoadDraft] Using prop formData, skipping draft load.');
        // If the prop contains photo data, set it.
        if (propFormData.photo) {
          setPhoto(propFormData.photo);
        }
        return;
      }

      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[AppFormPage2 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          // Assuming fetchApplicationDetails returns an object with a formData property
          // and formData itself contains a 'photo' field (base64 string)
          const appData = await fetchApplicationDetails(applicationId);
          // fetchApplicationDetails now directly returns ApplicationDetails (which includes formData)
          if (appData && appData.formData) {
            // Ensure that file proof fields are treated as strings (filenames) if they exist
            const loadedFormData = {
              ...appData.formData,
              dobProofFile: appData.formData.dobProofFile || null, // Expecting filename or null
              qualificationProofFile: appData.formData.qualificationProofFile || null,
              addressProofFile: appData.formData.addressProofFile || null,
            };
            console.log('[AppFormPage2 LoadDraft] appData.formData.dobProofFile:', appData.formData.dobProofFile);
            console.log('[AppFormPage2 LoadDraft] appData.formData.qualificationProofFile:', appData.formData.qualificationProofFile);
            console.log('[AppFormPage2 LoadDraft] appData.formData.addressProofFile:', appData.formData.addressProofFile);
            console.log('[AppFormPage2 LoadDraft] Constructed loadedFormData before setFormData:', JSON.stringify(loadedFormData, null, 2));
            setFormData(loadedFormData);
            if (appData.formData.photo) {
              setPhoto(appData.formData.photo); // Set the photo state
            }
            console.log(`[AppFormPage2 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[AppFormPage2 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[AppFormPage2 LoadDraft] Error loading draft data for ${applicationId}:`, error);
          // Optionally, set an error message for the user
        }
      }
    };

    loadDraftData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, isViewMode, propFormData]); // Run when applicationId, isViewMode, or propFormData changes

  // Effect to check if application is locked (after payment or manually locked by admin)
  useEffect(() => {
    const checkLockStatus = async () => {
      if (!applicationId) return;
      try {
        const response = await axios.get(`${API_BASE}/api/settings/application-lock-status/${applicationId}`);
        if (response.data.success && response.data.isLocked) {
          setIsApplicationLocked(true);
          console.log(`[AppFormPage2] Application ${applicationId} is locked: ${response.data.reason}`);
        } else {
          setIsApplicationLocked(false);
        }
      } catch (error) {
        console.error('[AppFormPage2] Error checking application lock status:', error);
      }
    };
    checkLockStatus();
  }, [applicationId]);

  // Fetch mandatory fields settings from admin settings with polling for realtime updates
  useEffect(() => {
    const fetchMandatoryFieldsSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const settingsResponse = await fetch(`${API_BASE}/api/public/settings`);
        if (settingsResponse.ok) {
          const data = await settingsResponse.json();
          const settings = data.settings || data;
          if (settings && settings.mandatoryFieldsPage2) {
            setMandatoryFieldsPage2(settings.mandatoryFieldsPage2);
            console.log('[AppFormPage2] Loaded mandatory fields settings from admin:', settings.mandatoryFieldsPage2);
          }
        }
      } catch (error) {
        console.error('[AppFormPage2] Error fetching mandatory fields settings:', error);
      }
    };
    
    // Initial fetch
    fetchMandatoryFieldsSettings();
    
    // Poll every 3 seconds for realtime updates when admin changes settings
    const pollInterval = setInterval(fetchMandatoryFieldsSettings, 3000);
    
    return () => clearInterval(pollInterval);
  }, []);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleOpenFilePreview = (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    console.log('[handleOpenFilePreview] Current API_BASE_URL:', API_BASE_URL);

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      // 'file' is the path from the DB, e.g., '/uploads/...'
      console.log('[handleOpenFilePreview] File is a string (path):', file);
      
      // Determine backend origin consistently using REACT_APP_API_URL if absolute; otherwise fallback to current origin
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const parsed = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${parsed.protocol}//${parsed.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      }

      // Normalize file to a path (strip any accidental origin and ensure leading slash; add /uploads when only filename)
      let normalizedPath = file;
      if (file.includes('://')) {
        try {
          normalizedPath = new URL(file).pathname;
        } catch {
          normalizedPath = file;
        }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.startsWith('uploads/') ? `/${normalizedPath}` : `/uploads/${normalizedPath}`;
      }

      url = `${serverBaseForFiles}${normalizedPath}`;
      name = normalizedPath.split('/').pop() || defaultFileName; // Extract filename for display

      // Infer mime type from extension
      if (file.toLowerCase().endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (file.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = file.split('.').pop()?.toLowerCase();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream'; // Fallback
      }

      console.log(`[handleOpenFilePreview] Constructed URL: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      console.log('[handleOpenFilePreview] File is a File object:', file);
      // File is a File object (newly uploaded)
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] File object - Constructed URL:', url, 'Name:', name, 'Type:', type);
    }

    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setModalFieldName(defaultFileName);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    try {
      if (formRef.current) {
        const canvas = await html2canvas(formRef.current, {
          scale: 2, // Reduced scale for smaller file size
          useCORS: true,
          logging: false,
          width: formRef.current.scrollWidth,
          height: formRef.current.scrollHeight,
          windowWidth: formRef.current.scrollWidth,
          windowHeight: formRef.current.scrollHeight,
        });

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        // Create a PDF with dimensions matching the canvas
        const pdf = new jsPDF({
          orientation: canvasWidth > canvasHeight ? 'l' : 'p',
          unit: 'pt',
          format: [canvasWidth, canvasHeight],
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.3); // Use a more aggressive compression with quality 0.3
        pdf.addImage(imgData, 'JPEG', 0, 0, canvasWidth, canvasHeight);
 
        // Simulate compression process for visual feedback
        for (let i = 0; i <= 100; i += 10) {
          setCompressionProgress(i);
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        pdf.save(`application-form-${applicationId || 'draft'}.pdf`);

      } else {
        console.error("Form reference not found for PDF generation.");
        alert("Could not find form content to generate PDF.");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };



  // Function to save data to database
  const handleProofFileUpload = async (file: File, fieldName: UploadableField) => {
    if (!applicationId) {
      console.error("Application ID is not set. Cannot upload file.");
      setUploadMessage(prev => ({ ...prev, [fieldName]: "Application ID not found. Please refresh." }));
      setTimeout(() => setUploadMessage(prev => ({ ...prev, [fieldName]: '' })), 5000);
      return;
    }

    // Global file type validation - block unsupported file types
    const blockedExtensions = ['.doc', '.docx', '.xlsx', '.xls', '.txt', '.bmp'];
    const blockedMimeTypes = [
      'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'image/bmp'
    ];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (blockedExtensions.includes(fileExtension) || blockedMimeTypes.includes(file.type)) {
      setUploadErrorModal({
        show: true,
        message: `File type "${fileExtension}" is not supported. Please upload only JPG, JPEG, PNG, or PDF files.`,
        fieldName
      });
      return;
    }

    setUploadStatus(prev => ({ ...prev, [fieldName]: 'uploading' }));
    setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
    setUploadMessage(prev => ({ ...prev, [fieldName]: '' }));

    try {
            const uploadResponse = await uploadFormFile(applicationId, file, fieldName, (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(prev => ({ ...prev, [fieldName]: percentComplete }));
        }
      });

      console.log(`[Upload Success] Field: ${fieldName}, API Response:`, uploadResponse);
      // Extract just the file path from the API response
      const filePath = uploadResponse.filePath;
      console.log(`[Upload Success] Extracted File Path:`, filePath);
      
      const updatedFormData = { ...formData, [fieldName]: filePath };
      setFormData(updatedFormData);
      console.log(`[Debug] Updated formData for ${fieldName}:`, updatedFormData[fieldName]);
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'success' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: 'File uploaded successfully!' }));
      
      // Auto-save the form data after successful upload
      saveToDatabaseCallback(updatedFormData, true);

    } catch (error) {
      console.error(`File upload failed for ${fieldName}:`, error);
      const errorMessage = (error as any).response?.data?.message || (error as any).message || 'File upload failed. Please try again.';
      setUploadStatus(prev => ({ ...prev, [fieldName]: 'error' }));
      setUploadMessage(prev => ({ ...prev, [fieldName]: errorMessage }));
      setUploadProgress(prev => ({ ...prev, [fieldName]: 0 }));
      
      // Show error modal for better user feedback
      setUploadErrorModal({ show: true, message: errorMessage, fieldName });
    } finally {
        // Increased timeout to 8 seconds for better user visibility
        console.log(`[Debug] Setting timeout for ${fieldName}. Current status: ${uploadStatus[fieldName]}`);
        setTimeout(() => {
            console.log(`[Debug Timeout] Clearing message for ${fieldName}. Current status: ${uploadStatus[fieldName]}`);
            setUploadMessage(prev => ({ ...prev, [fieldName]: '' }));

            // Do NOT reset status to idle to ensure link remains visible
            // Keeping uploadStatus as 'success' ensures the link will display
            console.log(`[Debug Timeout] Finished timeout for ${fieldName}`);
        }, 500);
    }
  };

  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      // Potentially show a more user-friendly error or guide them
      return;
    }

    if (!isOnline) {
      // TODO: Implement offline queuing if desired. For now, just prevent save.
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.'); // Placeholder message
      // setTimeout(() => setShowSaveIndicator(false), 3000); // Hide after a bit
      return; // Do not proceed with saving if offline
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      // Only send data that can be serialized (remove File objects)
      const dataToSave = {
        ...data, // data is the current formData state
        photo: photo, // Include the photo state (base64 string)
        // Handle file proofs: if it's a File object, take its name; if it's already a string (from loaded draft), keep it.
        dobProofFile: data.dobProofFile instanceof File ? data.dobProofFile.name : data.dobProofFile,
        qualificationProofFile: data.qualificationProofFile instanceof File ? data.qualificationProofFile.name : data.qualificationProofFile,
        addressProofFile: data.addressProofFile instanceof File ? data.addressProofFile.name : data.addressProofFile,
        landPlotDrawingFile: data.landPlotDrawingFile instanceof File ? data.landPlotDrawingFile.name : data.landPlotDrawingFile,
      };
      
      const response = await saveFormData(idToUse, dataToSave, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false; // Reset flag after successful initial draft save
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline, formData, photo]); // Added dependencies: applicationId, isOnline, formData, photo.

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[AppFormPage2 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId); // true for isDraft
      // isNewlyGeneratedIdRef.current = false; // Moved reset to saveToDatabaseCallback on success
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline, isNewlyGeneratedIdRef]);

  // Debounced auto save function for subsequent changes
  const handleFillDummyData = () => {
    console.log('[AppFormPage2 FillDummyData] Filling form with dummy data.');
    setFormData(prevData => ({
      ...prevData,
      // Page 2 specific fields - BUSINESS / EMPLOYMENT DETAILS
      presentOccupation: prevData.presentOccupation || 'Business',
      businessEmploymentDetails: prevData.businessEmploymentDetails || 'Running a retail business for 10 years',
      totalWorkExperienceYears: prevData.totalWorkExperienceYears || '15',
      relevantIndustryExperience: prevData.relevantIndustryExperience || '5 years in petroleum retail',
      // FINANCIAL CAPABILITY
      annualIncome: prevData.annualIncome || '2500000',
      netWorthAtApplication: prevData.netWorthAtApplication || '15000000',
      sourceOfInvestment: prevData.sourceOfInvestment || 'Own Funds',
      abilityToMeetRequiredInvestment: prevData.abilityToMeetRequiredInvestment || 'Yes',
      // LAND DETAILS
      landOwnershipStatus: prevData.landOwnershipStatus || 'Owned',
      landRegistrationDate: prevData.landRegistrationDate || '2020-01-15',
      landCompleteAddress: prevData.landCompleteAddress || '123 Industrial Area, Sector 5, Mumbai',
      landLocationCoordinates: prevData.landLocationCoordinates || '19.0760° N, 72.8777° E',
      // Ensure applicationNoOfficeUse is preserved
      applicationNoOfficeUse: prevData.applicationNoOfficeUse || applicationId || '',
      errors: {},
    }));
    setSaveMessage('Dummy data filled. Save to persist.');
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
    }, 3000);
  };

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      // Save to database if we have an email (used as identifier) and a valid application ID
      // All auto-saves should be drafts until explicit submission
      if (applicationId && newData.emailId && newData.emailId.trim() !== '') {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}, Email: ${newData.emailId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}, Email: ${newData.emailId ? newData.emailId.trim() : 'undefined/empty'}`);
      }
    }, 1000); // 1 second delay
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  // Validation function for mandatory fields on Page 2
  // Only shakes the FIRST invalid field, scrolls to it, and returns false
  const validateMandatoryFieldsPage2 = (): boolean => {
    // Helper to trigger shake animation
    const triggerShake = (setShaking: React.Dispatch<React.SetStateAction<boolean>>) => {
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    };

    // Land Ownership Status
    if (mandatoryFieldsPage2.landOwnershipStatus && !formData.landOwnershipStatus) {
      triggerShake(setIsLandOwnershipStatusShaking);
      landOwnershipStatusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Period of Lease
    if (mandatoryFieldsPage2.periodOfLease && !formData.periodOfLeaseYears) {
      triggerShake(setIsPeriodOfLeaseShaking);
      periodOfLeaseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Complete Address
    if (mandatoryFieldsPage2.landCompleteAddress && !formData.landCompleteAddress) {
      triggerShake(setIsLandCompleteAddressShaking);
      landCompleteAddressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Location Coordinates
    if (mandatoryFieldsPage2.landLocationCoordinates && !formData.landLocationCoordinates) {
      triggerShake(setIsLandLocationCoordinatesShaking);
      landLocationCoordinatesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Location Coordinates format validation (must be Google coordinates or maps.app.goo.gl URL)
    if (formData.landLocationCoordinates && formData.errors?.landLocationCoordinates) {
      triggerShake(setIsLandLocationCoordinatesShaking);
      landLocationCoordinatesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Plot Drawing File
    if (mandatoryFieldsPage2.landPlotDrawingFile && !formData.landPlotDrawingFile) {
      triggerShake(setIsLandPlotDrawingFileShaking);
      landPlotDrawingFileRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Survey No
    if (mandatoryFieldsPage2.landSurveyNo && !formData.landSurveyNo) {
      triggerShake(setIsLandSurveyNoShaking);
      landSurveyNoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Khata No
    if (mandatoryFieldsPage2.landKhataNo && !formData.landKhataNo) {
      triggerShake(setIsLandKhataNoShaking);
      landKhataNoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Khatauni No
    if (mandatoryFieldsPage2.landKhatauniNo && !formData.landKhatauniNo) {
      triggerShake(setIsLandKhatauniNoShaking);
      landKhatauniNoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Ferfar No
    if (mandatoryFieldsPage2.landFerfarNo && !formData.landFerfarNo) {
      triggerShake(setIsLandFerfarNoShaking);
      landFerfarNoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Varga No
    if (mandatoryFieldsPage2.landVargaNo && !formData.landVargaNo) {
      triggerShake(setIsLandVargaNoShaking);
      landVargaNoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Frontage - check empty OR below minimum
    if (mandatoryFieldsPage2.mnglLandFrontageMtrs) {
      if (!formData.landFrontageMtrs) {
        triggerShake(setIsMnglLandFrontageMtrsShaking);
        mnglLandFrontageMtrsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      // Also check if below minimum threshold
      const minFrontage = parseFloat(formData.locationMinPlotFrontage || '0');
      const currentFrontage = parseFloat(formData.landFrontageMtrs || '0');
      if (minFrontage > 0 && currentFrontage < minFrontage) {
        triggerShake(setIsMnglLandFrontageMtrsShaking);
        mnglLandFrontageMtrsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
    }

    // Depth
    if (mandatoryFieldsPage2.mnglLandDepthMtrs && !formData.landDepthMtrs) {
      triggerShake(setIsMnglLandDepthMtrsShaking);
      mnglLandDepthMtrsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Total Area - check empty OR below minimum (20 * 31 = 620 sq mtrs)
    if (mandatoryFieldsPage2.mnglTotalAreaSqMtrs) {
      if (!formData.landAreaSqMtrs) {
        triggerShake(setIsMnglTotalAreaSqMtrsShaking);
        mnglTotalAreaSqMtrsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      // Also check if below minimum threshold (620 sq mtrs based on 20 * 31)
      const minFrontage = parseFloat(formData.locationMinPlotFrontage || '0');
      const minDepth = parseFloat(formData.locationMinPlotDepth || '0');
      const minArea = minFrontage > 0 && minDepth > 0 ? minFrontage * minDepth : 620;
      const currentArea = parseFloat(formData.landAreaSqMtrs || '0');
      if (currentArea < minArea) {
        triggerShake(setIsMnglTotalAreaSqMtrsShaking);
        mnglTotalAreaSqMtrsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
    }

    // Approach Road Width
    if (mandatoryFieldsPage2.landApproachRoadWidthMtrs && !formData.landApproachRoadWidthMtrs) {
      triggerShake(setIsLandApproachRoadWidthMtrsShaking);
      landApproachRoadWidthMtrsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Plot Level Type
    if (mandatoryFieldsPage2.landPlotLevelType && !formData.landPlotLevelType) {
      triggerShake(setIsLandPlotLevelTypeShaking);
      landPlotLevelTypeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Surroundings East
    if (mandatoryFieldsPage2.surroundingsEast && !formData.surroundingsEast) {
      triggerShake(setIsSurroundingsEastShaking);
      surroundingsEastRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Surroundings West
    if (mandatoryFieldsPage2.surroundingsWest && !formData.surroundingsWest) {
      triggerShake(setIsSurroundingsWestShaking);
      surroundingsWestRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Surroundings North
    if (mandatoryFieldsPage2.surroundingsNorth && !formData.surroundingsNorth) {
      triggerShake(setIsSurroundingsNorthShaking);
      surroundingsNorthRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Surroundings South
    if (mandatoryFieldsPage2.surroundingsSouth && !formData.surroundingsSouth) {
      triggerShake(setIsSurroundingsSouthShaking);
      surroundingsSouthRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Use
    if (mandatoryFieldsPage2.landUse && !formData.landUse) {
      triggerShake(setIsLandUseShaking);
      landUseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Title Clear
    if (mandatoryFieldsPage2.landTitleClear && !formData.landTitleClear) {
      triggerShake(setIsLandTitleClearShaking);
      landTitleClearRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Title Clear Details (required if No is selected)
    if (formData.landTitleClear === 'No' && !formData.landTitleClearDetails?.trim()) {
      triggerShake(setIsLandTitleClearShaking);
      landTitleClearRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Litigation Free
    if (mandatoryFieldsPage2.landLitigationFree && !formData.landLitigationFree) {
      triggerShake(setIsLandLitigationFreeShaking);
      landLitigationFreeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Litigation Free Details (required if No is selected)
    if (formData.landLitigationFree === 'No' && !formData.landLitigationFreeDetails?.trim()) {
      triggerShake(setIsLandLitigationFreeShaking);
      landLitigationFreeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Power Connection
    if (mandatoryFieldsPage2.landPowerConnectionAvailable && !formData.landPowerConnectionAvailable) {
      triggerShake(setIsLandPowerConnectionAvailableShaking);
      landPowerConnectionAvailableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Water Connection
    if (mandatoryFieldsPage2.landWaterConnectionAvailable && !formData.landWaterConnectionAvailable) {
      triggerShake(setIsLandWaterConnectionAvailableShaking);
      landWaterConnectionAvailableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Encroachment
    if (mandatoryFieldsPage2.landEncroachment && !formData.landEncroachment) {
      triggerShake(setIsLandEncroachmentShaking);
      landEncroachmentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Encroachment Details (required if Yes is selected)
    if (formData.landEncroachment === 'Yes' && !formData.landEncroachmentDetails?.trim()) {
      triggerShake(setIsLandEncroachmentShaking);
      landEncroachmentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Govt Acquisition Plan
    if (mandatoryFieldsPage2.landGovtAcquisitionPlan && !formData.landGovtAcquisitionPlan) {
      triggerShake(setIsLandGovtAcquisitionPlanShaking);
      landGovtAcquisitionPlanRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Govt Acquisition Plan Details (required if Yes is selected)
    if (formData.landGovtAcquisitionPlan === 'Yes' && !formData.landGovtAcquisitionPlanDetails?.trim()) {
      triggerShake(setIsLandGovtAcquisitionPlanShaking);
      landGovtAcquisitionPlanRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Nearest Police Station Name
    if (mandatoryFieldsPage2.nearestPoliceStationName && !formData.nearestPoliceStationName) {
      triggerShake(setIsNearestPoliceStationNameShaking);
      nearestPoliceStationNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Nearest Fire Station Name
    if (mandatoryFieldsPage2.nearestFireStationName && !formData.nearestFireStationName) {
      triggerShake(setIsNearestFireStationNameShaking);
      nearestFireStationNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // High Voltage Transmission Line
    if (mandatoryFieldsPage2.highVoltageTransmissionLine && !formData.highVoltageTransmissionLine) {
      triggerShake(setIsHighVoltageTransmissionLineShaking);
      highVoltageTransmissionLineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // High Voltage Transmission Line Distance (required if Yes is selected)
    if (formData.highVoltageTransmissionLine === 'Yes' && !formData.highVoltageTransmissionLineDistance?.trim()) {
      triggerShake(setIsHighVoltageTransmissionLineShaking);
      highVoltageTransmissionLineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Abutting Road Boundary
    if (mandatoryFieldsPage2.landAbuttingRoadBoundary && !formData.landAbuttingRoadBoundary) {
      triggerShake(setIsLandAbuttingRoadBoundaryShaking);
      landAbuttingRoadBoundaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // Land Abutting Road Boundary Distance (required if Yes is selected)
    if (formData.landAbuttingRoadBoundary === 'Yes' && !formData.landAbuttingRoadBoundaryDistance?.trim()) {
      triggerShake(setIsLandAbuttingRoadBoundaryShaking);
      landAbuttingRoadBoundaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    // All fields valid
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let newErrors = { ...formData.errors };
    let updatedFormData;
    
    // Auto convert name fields to uppercase
    if (name === 'nameInBlocks' || name === 'fatherOrHusbandName') {
      updatedFormData = { ...formData, [name]: value.toUpperCase() };
      setFormData(updatedFormData);
    } 
    // Validate mobile numbers
    else if (name === 'personalMobile' || name === 'secondaryMobile') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue !== '' && numericValue.length !== 10) {
        newErrors[name] = 'Must be exactly 10 digits';
      } else {
        delete newErrors[name];
      }
      updatedFormData = { 
        ...formData, 
        [name]: numericValue,
        errors: newErrors
      };
      setFormData(updatedFormData);
    }
    // Email validation
    else if (name === 'emailId') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value !== '' && !emailRegex.test(value)) {
        newErrors.emailId = 'Please enter a valid email address';
      } else {
        delete newErrors.emailId;
      }
      updatedFormData = { 
        ...formData, 
        [name]: value,
        errors: newErrors
      };
      setFormData(updatedFormData);
    }
    // Land Location Coordinates validation - must be Google coordinates or maps.app.goo.gl URL
    else if (name === 'landLocationCoordinates') {
      // Google coordinates pattern: latitude, longitude (e.g., "19.0760, 72.8777" or "19.0760,72.8777")
      const coordinatesRegex = /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/;
      // Google Maps short URL pattern
      const googleMapsUrlRegex = /^https:\/\/maps\.app\.goo\.gl\/.+$/;
      
      if (value !== '' && !coordinatesRegex.test(value.trim()) && !googleMapsUrlRegex.test(value.trim())) {
        newErrors.landLocationCoordinates = 'Please enter valid Google coordinates (e.g., 19.0760, 72.8777) or a Google Maps URL (https://maps.app.goo.gl/...)';
      } else {
        delete newErrors.landLocationCoordinates;
      }
      updatedFormData = { 
        ...formData, 
        [name]: value,
        errors: newErrors
      };
      setFormData(updatedFormData);
    }
    else {
      updatedFormData = { ...formData, [name]: value };
      setFormData(updatedFormData);
    }
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhoto(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    let animationFrameId: number;
    let messageTimerId: NodeJS.Timeout;
    let clearMessageTimerId: NodeJS.Timeout;

    if (photo) {
      setIsRevealing(false); 
      setPhotoUploadMessage(''); 

      animationFrameId = requestAnimationFrame(() => {
        setIsRevealing(true); // Start animation
        messageTimerId = setTimeout(() => {
          setPhotoUploadMessage('Photo uploaded successfully!');
          clearMessageTimerId = setTimeout(() => {
            setPhotoUploadMessage('');
          }, 3000); // Clear message 3 seconds after it appears
        }, 2000); // Message appears after 2s animation (matching animation duration)
      });
    } else {
      setIsRevealing(false);
      setPhotoUploadMessage('');
    }

    return () => { // Cleanup function
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (messageTimerId) clearTimeout(messageTimerId);
      if (clearMessageTimerId) clearTimeout(clearMessageTimerId);
    };
  }, [photo]);

  useEffect(() => {
    const currentEditingAppId = sessionStorage.getItem('currentApplicationId');
    const newAppIdFromState = location.state?.newApplicationId as string;

    let determinedAppId = '';
    if (currentEditingAppId) {
      determinedAppId = currentEditingAppId;
    } else if (newAppIdFromState) {
      determinedAppId = newAppIdFromState;
    }

    if (determinedAppId) {
      setApplicationId(determinedAppId);
      setFormData(prevData => ({
        ...prevData,
        applicationNoOfficeUse: determinedAppId
      }));
    } else {
      // Handle case where no ID is available (e.g., direct navigation to form, might be an error or new flow)
      console.warn('Application ID not found in session storage or location state.');
      // Optionally, navigate away or show an error
    }
  }, [location.state]);

  // The following useEffect was made redundant by the one at ~line 972 and has been removed.
  // It was causing double initial save attempts.
  // useEffect(() => {
  //   if (isNewlyGeneratedIdRef.current && applicationId && !isSaving && isOnline) {
  //     console.log(`Auto-saving newly generated application ${applicationId} as draft.`);
  //     const initialDraftData = { 
  //       ...formData, 
  //       applicationNoOfficeUse: applicationId, 
  //       emailId: formData.emailId
  //     };
  //     saveToDatabaseCallback(initialDraftData, true, applicationId);
  //     isNewlyGeneratedIdRef.current = false;
  //   }
  // }, [applicationId, formData.emailId, isNewlyGeneratedIdRef.current, isSaving, isOnline, saveToDatabaseCallback]);

  const triggerPhotoUpload = () => {
    photoInputRef.current?.click();
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };



  const handleNextButtonClick = () => {
    // Validate mandatory fields before navigation
    if (!validateMandatoryFieldsPage2()) {
      return; // Validation failed, shake animation triggered
    }
    
    // Show save modal before navigation (skip if application is locked)
    if (applicationId && formData.emailId && formData.emailId.trim() !== '' && isOnline && !isApplicationLocked) {
      setPendingNavigation('/form/page3');
      setShowSaveModal(true);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate('/form/page3', { state: { applicationId } });
    }
  };

  // Handle save modal actions
  const handleSaveAndNavigate = async () => {
    setShowSaveModal(false);
    
    if (applicationId && isOnline) {
      console.log('[Navigation Page2] Saving draft before navigating');
      await saveToDatabaseCallback(formData, true, applicationId);
      
      // Wait 1.5 seconds so user can see the "Draft saved successfully" message
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
    
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleSkipSaveAndNavigate = () => {
    setShowSaveModal(false);
    if (pendingNavigation) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  // Handle navigation from vertical nav bar - show save modal
  const handleNavBarNavigation = (targetPath: string) => {
    // Only validate when navigating forward (to page 3 or beyond, including pageA)
    if (targetPath.includes('page3') || targetPath.includes('page4') || targetPath.includes('page5') || 
        targetPath.includes('pageA') || targetPath.includes('appendix') || parseInt(targetPath.replace(/\D/g, '')) > 2) {
      if (!validateMandatoryFieldsPage2()) {
        return; // Validation failed, shake animation triggered
      }
    }
    
    // Skip save modal if application is locked
    if (applicationId && isOnline && !isApplicationLocked) {
      setPendingNavigation(targetPath);
      setShowSaveModal(true);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
      navigate(targetPath, { state: { applicationId } });
    }
  };

  // Form progress is now calculated by the shared useFormProgress hook (global across Page1+2+3)

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <>
      {/* Global shake animation keyframes */}
      <GlobalShakeStyle />
      
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel>Page 2/5 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      {/* Application Locked Banner */}
      {isApplicationLocked && !isViewMode && (
        <div style={{
          position: 'fixed',
          top: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(254, 243, 199, 0.92)',
          backdropFilter: 'blur(8px)',
          border: '2px solid #f59e0b',
          borderRadius: '12px',
          padding: '10px 20px',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 4px 20px rgba(245, 158, 11, 0.25)',
          maxWidth: '90%'
        }}>
          <span style={{ fontSize: '1.3rem' }}>🔒</span>
          <div>
            <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.9rem' }}>
              Application Locked
            </div>
            <div style={{ color: '#78350f', fontSize: '0.75rem' }}>
              This application is locked. You can view data but cannot make changes.
            </div>
          </div>
        </div>
      )}
      <FloatingBackButton onClick={() => { window.scrollTo({ top: 0, behavior: 'auto' }); navigate('/form/page1'); }} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      <div style={{ position: 'fixed', top: '80px', left: '30px', zIndex: 1001 }}>
        <button 
          onClick={() => setShowBrochureModal(true)}
          style={{ 
            backgroundColor: '#2196f3', 
            color: '#ffffff', 
            borderRadius: '4px', 
            padding: '8px 15px',
            border: '1px solid #1976d2',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          📄 View Brochure
        </button>
      </div>
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      <FormPageNavigator currentPage={2} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} onNavigate={handleNavBarNavigation} />
      <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page →
        </NextPageButton>
      </div>
      {/* WhatsApp Help Button */}
      <div style={{ position: 'fixed', right: '20px', bottom: '100px', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#25D366', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>Need Help!</span>
        <a 
          href="https://web.whatsapp.com/send?phone=919971874909&text=Hi%2C%20Need%20Help%20filling%20MNGL%20DODO%20form"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'block', cursor: 'pointer', transition: 'transform 0.2s ease' }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          title="Chat with us on WhatsApp"
        >
          <img src="/assets/images/whatsapp.png" alt="WhatsApp Help" style={{ }} />
        </a>
      </div>
      <PageWrapper scale={zoomScale}>
      <A4PageContainer ref={formRef}>
        <FormContentContainer>
          {/* LAND DETAILS (MANDATORY) Section */}
          <FormFieldFullWidth style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <Label style={{ fontWeight: 700, marginBottom: '20px', color: '#1a5276', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '1.1em', textAlign: 'center', textDecoration: 'underline' }}>
              OFFERED LAND/PLOT OWNERSHIP DETAILS (MANDATORY)
              <span 
                title="Offered Land/Plot To be given to MNGL" 
                style={{ cursor: 'help', fontSize: '0.9em', color: '#3498db' }}
              >
                ⓘ
              </span>
            </Label>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr ref={landOwnershipStatusRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '35%' }}>
                    Offered Land/Plot Ownership Status
                    {mandatoryFieldsPage2.landOwnershipStatus && !formData.landOwnershipStatus && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandOwnershipStatusShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '25px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landOwnershipStatus" value="Owned" checked={formData.landOwnershipStatus === 'Owned'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Owned
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landOwnershipStatus" value="Co Ownership" checked={formData.landOwnershipStatus === 'Co Ownership'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Co-Ownership (With One Partner)
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landOwnershipStatus" value="Lease" checked={formData.landOwnershipStatus === 'Lease'} onChange={(e) => { if (e.target.checked) { setShowLeaseModal(true); } }} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Lease
                      </label>
                    </div>
                  </td>
                </tr>
                <tr ref={periodOfLeaseRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Period of Land Lease to be given to MNGL <span style={{ fontWeight: 400, fontSize: '0.85em', color: '#e74c3c' }}>(minimum 15 years, maximum 28 years 11 months 30 days)</span>
                    {mandatoryFieldsPage2.periodOfLease && !formData.periodOfLeaseYears && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isPeriodOfLeaseShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <StyledInput
                          type="number"
                          name="periodOfLeaseYears"
                          value={formData.periodOfLeaseYears || ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (e.target.value === '' || (value >= 0 && value <= 28)) {
                              handleChange(e);
                            }
                          }}
                          onBlur={(e) => {
                            const value = parseInt(e.target.value);
                            if (value && value < 15) {
                              handleChange({ target: { name: 'periodOfLeaseYears', value: '15' } } as any);
                            } else if (value && value > 28) {
                              handleChange({ target: { name: 'periodOfLeaseYears', value: '28' } } as any);
                            }
                          }}
                          min="15"
                          max="28"
                          placeholder="Years"
                          disabled={!isOnline || isViewMode || isApplicationLocked}
                          style={{ margin: 0, width: '70px', textAlign: 'center' }}
                        />
                        <span style={{ fontSize: '0.9em' }}>Years</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <StyledInput
                          type="number"
                          name="periodOfLeaseMonths"
                          value={formData.periodOfLeaseMonths || ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (e.target.value === '' || (value >= 0 && value <= 11)) {
                              handleChange(e);
                            }
                          }}
                          min="0"
                          max="11"
                          placeholder="Months"
                          disabled={!isOnline || isViewMode || isApplicationLocked}
                          style={{ margin: 0, width: '70px', textAlign: 'center' }}
                        />
                        <span style={{ fontSize: '0.9em' }}>Months</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <StyledInput
                          type="number"
                          name="periodOfLeaseDays"
                          value={formData.periodOfLeaseDays || ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (e.target.value === '' || (value >= 0 && value <= 30)) {
                              handleChange(e);
                            }
                          }}
                          min="0"
                          max="30"
                          placeholder="Days"
                          disabled={!isOnline || isViewMode || isApplicationLocked}
                          style={{ margin: 0, width: '70px', textAlign: 'center' }}
                        />
                        <span style={{ fontSize: '0.9em' }}>Days</span>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr ref={landPlotDrawingFileRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Attach plot drawing/report indicating the CNG plot marking
                    {mandatoryFieldsPage2.landPlotDrawingFile && !formData.landPlotDrawingFile && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandPlotDrawingFileShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <input type="file" id="landPlotDrawingFile" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={(e) => { if (e.target.files?.[0]) handleProofFileUpload(e.target.files[0], 'landPlotDrawingFile'); }} disabled={!isOnline || isViewMode || isApplicationLocked} />
                    
                    {formData.landPlotDrawingFile ? (
                      /* File uploaded - show organized layout with thumbnail */
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        {/* Left side - File info and actions */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {/* File link */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.85em', color: '#555', fontWeight: 500 }}>📎 File:</span>
                            <ClickableFileName 
                              onClick={() => handleOpenFilePreview(formData.landPlotDrawingFile, 'landPlotDrawingFile')}
                              style={{ fontSize: '0.85em', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              title={typeof formData.landPlotDrawingFile === 'string' ? formData.landPlotDrawingFile : formData.landPlotDrawingFile.name}
                            >
                              {typeof formData.landPlotDrawingFile === 'string' 
                                ? formData.landPlotDrawingFile.split('/').pop() 
                                : formData.landPlotDrawingFile.name}
                            </ClickableFileName>
                          </div>
                          
                          {/* Change button only */}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <FileUploadButton 
                              onClick={() => document.getElementById('landPlotDrawingFile')?.click()} 
                              disabled={!isOnline || isViewMode || uploadStatus['landPlotDrawingFile'] === 'uploading'}
                              style={{ 
                                opacity: uploadStatus['landPlotDrawingFile'] === 'uploading' ? 0.7 : 1,
                                padding: '6px 14px',
                                fontSize: '0.85em'
                              }}
                            >
                              {uploadStatus['landPlotDrawingFile'] === 'uploading' ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <UploadSpinner />
                                  {uploadProgress['landPlotDrawingFile'] || 0}%
                                </span>
                              ) : '🔄 Change'}
                            </FileUploadButton>
                          </div>
                        </div>
                        
                        {/* Right side - Thumbnail preview (clickable) with pop animation */}
                        <ThumbnailContainer
                          key={typeof formData.landPlotDrawingFile === 'string' ? formData.landPlotDrawingFile : 'thumbnail'}
                          onClick={() => handleOpenFilePreview(formData.landPlotDrawingFile, 'landPlotDrawingFile')}
                          title="Click to view full size"
                        >
                          {(() => {
                            const fileValue = formData.landPlotDrawingFile;
                            const fileStr = typeof fileValue === 'string' ? fileValue : '';
                            const isImage = fileStr.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
                            
                            if (isImage && fileStr) {
                              // Construct proper image URL using same logic as handleOpenFilePreview
                              const rawApiBaseUrl = process.env.REACT_APP_API_URL || '';
                              let serverBase = window.location.origin;
                              if (rawApiBaseUrl.startsWith('http')) {
                                try {
                                  const parsed = new URL(rawApiBaseUrl);
                                  serverBase = `${parsed.protocol}//${parsed.host}`;
                                } catch (e) {
                                  serverBase = window.location.origin;
                                }
                              }
                              // For localhost:3000, redirect to port 5000
                              if (serverBase.includes('localhost:3000')) {
                                serverBase = serverBase.replace(':3000', ':5000');
                              }
                              const imgPath = fileStr.startsWith('/') ? fileStr : `/${fileStr}`;
                              const fullUrl = `${serverBase}${imgPath}`;
                              
                              return (
                                <img 
                                  src={fullUrl}
                                  alt="Plot Drawing"
                                />
                              );
                            } else {
                              return (
                                <div style={{ textAlign: 'center', color: '#666', fontSize: '0.7em', padding: '6px' }}>
                                  📄<br/>PDF
                                </div>
                              );
                            }
                          })()}
                        </ThumbnailContainer>
                      </div>
                    ) : (
                      /* No file uploaded - show upload prompt */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ fontSize: '0.85em', color: '#888', fontStyle: 'italic' }}>
                          Upload the drawing in PDF/JPEG/PNG format
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <FileUploadButton 
                            onClick={() => document.getElementById('landPlotDrawingFile')?.click()} 
                            disabled={!isOnline || isViewMode || uploadStatus['landPlotDrawingFile'] === 'uploading'}
                            style={{ opacity: uploadStatus['landPlotDrawingFile'] === 'uploading' ? 0.7 : 1 }}
                          >
                            {uploadStatus['landPlotDrawingFile'] === 'uploading' ? (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <UploadSpinner />
                                Uploading... {uploadProgress['landPlotDrawingFile'] || 0}%
                              </span>
                            ) : '📤 Upload Drawing'}
                          </FileUploadButton>
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
                <tr ref={landCompleteAddressRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Complete Offered Land/Plot Address
                    {mandatoryFieldsPage2.landCompleteAddress && !formData.landCompleteAddress && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandCompleteAddressShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledTextArea name="landCompleteAddress" value={formData.landCompleteAddress || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, minHeight: '60px' }} />
                  </td>
                </tr>
                <tr ref={landLocationCoordinatesRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Offered Land/Plot Location
                      {mandatoryFieldsPage2.landLocationCoordinates && !formData.landLocationCoordinates && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowCoordinatesHelpModal(true)}
                        title="Click for detailed instructions on finding coordinates"
                        style={{ 
                          background: 'linear-gradient(135deg, #3498db, #2980b9)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 700,
                          boxShadow: '0 2px 4px rgba(52, 152, 219, 0.3)',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.15)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(52, 152, 219, 0.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(52, 152, 219, 0.3)';
                        }}
                      >
                        ?
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandLocationCoordinatesShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput 
                      type="text" 
                      name="landLocationCoordinates" 
                      value={formData.landLocationCoordinates || ''} 
                      onChange={handleChange} 
                      placeholder="Paste Google Maps coordinates (e.g., 19.0760, 72.8777) or Google Maps URL (https://maps.app.goo.gl/...)" 
                      disabled={!isOnline || isViewMode || isApplicationLocked} 
                      style={{ 
                        margin: 0, 
                        width: '100%',
                        borderColor: formData.errors?.landLocationCoordinates ? '#dc2626' : undefined
                      }} 
                    />
                    {formData.errors?.landLocationCoordinates && (
                      <div style={{ color: '#dc2626', fontSize: '0.75em', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>⚠️</span> {formData.errors.landLocationCoordinates}
                      </div>
                    )}
                  </td>
                </tr>
                <tr ref={landSurveyNoRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Survey No: / Gata No:
                    {mandatoryFieldsPage2.landSurveyNo && !formData.landSurveyNo && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandSurveyNoShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="landSurveyNo" value={formData.landSurveyNo || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%' }} />
                  </td>
                </tr>
                <tr ref={landKhataNoRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Khata No:
                    {mandatoryFieldsPage2.landKhataNo && !formData.landKhataNo && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandKhataNoShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="landKhataNo" value={formData.landKhataNo || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%' }} />
                  </td>
                </tr>
                <tr ref={landKhatauniNoRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Khatauni No:
                    {mandatoryFieldsPage2.landKhatauniNo && !formData.landKhatauniNo && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandKhatauniNoShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="landKhatauniNo" value={formData.landKhatauniNo || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%' }} />
                  </td>
                </tr>
                <tr ref={landFerfarNoRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Ferfar No:
                    {mandatoryFieldsPage2.landFerfarNo && !formData.landFerfarNo && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandFerfarNoShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="landFerfarNo" value={formData.landFerfarNo || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%' }} />
                  </td>
                </tr>
                <tr ref={landVargaNoRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Varga No:
                    {mandatoryFieldsPage2.landVargaNo && !formData.landVargaNo && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandVargaNoShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="landVargaNo" value={formData.landVargaNo || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%' }} />
                  </td>
                </tr>
                <tr ref={mnglLandFrontageMtrsRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Frontage (in Mtrs) of Offered Land/Plot to MNGL
                      {mandatoryFieldsPage2.mnglLandFrontageMtrs && !formData.landFrontageMtrs && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                      )}
                      {formData.locationFrom === 'All Roads' && formData.locationTo === 'All Roads' && (
                        <button
                          type="button"
                          onClick={() => setShowPlotSizeModal(true)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.1em',
                            padding: '2px',
                            color: '#3498db'
                          }}
                          title="View minimum plot size requirements"
                        >
                          ℹ️
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isMnglLandFrontageMtrsShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <StyledInput 
                        type="number" 
                        name="landFrontageMtrs" 
                        value={formData.landFrontageMtrs || ''} 
                        onChange={handleChange} 
                        placeholder="To be filled by applicant" 
                        disabled={!isOnline || isViewMode || isApplicationLocked} 
                        min={(() => { const v = parseFloat(formData.locationMinPlotFrontage || ''); return v > 0 ? v.toString() : '1'; })()}
                        style={{
                          margin: 0,
                          flex: 1,
                          borderColor: (() => {
                            const minF = parseFloat(formData.locationMinPlotFrontage || '');
                            return formData.landFrontageMtrs && minF > 0 && parseFloat(formData.landFrontageMtrs) < minF ? '#e74c3c' : undefined;
                          })()
                        }}
                      />
                      {formData.locationMinPlotFrontage && parseFloat(formData.locationMinPlotFrontage) > 0 && (
                        <span style={{ fontSize: '0.85em', color: '#666', whiteSpace: 'nowrap' }}>
                          Cannot be less than {formData.locationMinPlotFrontage}
                        </span>
                      )}
                    </div>
                    {formData.landFrontageMtrs && formData.locationMinPlotFrontage && (() => { const minF = parseFloat(formData.locationMinPlotFrontage); return minF > 0 && parseFloat(formData.landFrontageMtrs) < minF; })() && (
                      <div style={{ color: '#e74c3c', fontSize: '0.85em', marginTop: '5px' }}>
                        ⚠️ Value must be at least {formData.locationMinPlotFrontage} meters
                      </div>
                    )}
                  </td>
                </tr>
                <tr ref={mnglLandDepthMtrsRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Depth (in Mtrs) of Offered Land/Plot to MNGL
                      {mandatoryFieldsPage2.mnglLandDepthMtrs && !formData.landDepthMtrs && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                      )}
                      {formData.locationFrom === 'All Roads' && formData.locationTo === 'All Roads' && (
                        <button
                          type="button"
                          onClick={() => setShowPlotSizeModal(true)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.1em',
                            padding: '2px',
                            color: '#3498db'
                          }}
                          title="View minimum plot size requirements"
                        >
                          ℹ️
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isMnglLandDepthMtrsShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <StyledInput 
                        type="number" 
                        name="landDepthMtrs" 
                        value={formData.landDepthMtrs || ''} 
                        onChange={handleChange} 
                        placeholder="To be filled by applicant" 
                        disabled={!isOnline || isViewMode || isApplicationLocked} 
                        min="1"
                        style={{ 
                          margin: 0, 
                          flex: 1,
                          borderColor: formData.landDepthMtrs && parseFloat(formData.landDepthMtrs) < 1 ? '#e74c3c' : undefined 
                        }} 
                      />
                                          </div>
                    {formData.landDepthMtrs && parseFloat(formData.landDepthMtrs) < 1 && (
                      <div style={{ color: '#e74c3c', fontSize: '0.85em', marginTop: '5px' }}>
                        ⚠️ Value must be at least 1 meter
                      </div>
                    )}
                  </td>
                </tr>
                <tr ref={mnglTotalAreaSqMtrsRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Total Area (Sq. Mtrs) of offered Land/Plot to MNGL
                      {mandatoryFieldsPage2.mnglTotalAreaSqMtrs && !formData.landAreaSqMtrs && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                      )}
                      {formData.locationFrom === 'All Roads' && formData.locationTo === 'All Roads' && (
                        <button
                          type="button"
                          onClick={() => setShowPlotSizeModal(true)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.1em',
                            padding: '2px',
                            color: '#3498db'
                          }}
                          title="View minimum plot size requirements"
                        >
                          ℹ️
                        </button>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isMnglTotalAreaSqMtrsShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1, position: 'relative' }}>
                        <StyledInput 
                          type="number" 
                          name="landAreaSqMtrs" 
                          value={formData.landAreaSqMtrs || ''} 
                          onChange={handleChange}
                          placeholder="To be filled by applicant"
                          disabled={!isOnline || isViewMode || isApplicationLocked}
                          min={(() => { const f = parseFloat(formData.locationMinPlotFrontage || ''); const d = parseFloat(formData.locationMinPlotDepth || ''); return f > 0 && d > 0 ? (f * d).toString() : '1'; })()}
                          style={{
                            margin: 0,
                            width: '100%',
                            paddingRight: '35px',
                            borderColor: (() => {
                              if (!formData.landAreaSqMtrs || !formData.locationMinPlotFrontage || !formData.locationMinPlotDepth) return undefined;
                              const f = parseFloat(formData.locationMinPlotFrontage); const d = parseFloat(formData.locationMinPlotDepth);
                              const minA = f > 0 && d > 0 ? f * d : 620;
                              const area = parseFloat(formData.landAreaSqMtrs);
                              return area < minA ? '#e74c3c' : '#27ae60';
                            })()
                          }}
                        />
                        {formData.landAreaSqMtrs && formData.locationMinPlotFrontage && formData.locationMinPlotDepth && (
                          <span
                            title={(() => {
                              const f = parseFloat(formData.locationMinPlotFrontage); const d = parseFloat(formData.locationMinPlotDepth);
                              const minA = f > 0 && d > 0 ? f * d : 620;
                              const area = parseFloat(formData.landAreaSqMtrs);
                              const minLabel = f > 0 && d > 0 ? `${minA} Sq. Mtrs (Min Frontage ${formData.locationMinPlotFrontage} × Min Depth ${formData.locationMinPlotDepth})` : `${minA} Sq. Mtrs (default minimum as per Brochure)`;
                              return area >= minA
                                ? `✓ ACCEPTED: Your Total Land Area (${formData.landAreaSqMtrs} Sq. Mtrs) meets the minimum requirement of ${minLabel}.`
                                : `✗ REJECTED: Your Total Land Area (${formData.landAreaSqMtrs} Sq. Mtrs) is below the minimum requirement of ${minLabel}. Please enter a value of at least ${minA} Sq. Mtrs.`;
                            })()}
                            style={{
                              position: 'absolute',
                              right: '10px',
                              cursor: 'help',
                              fontSize: '1.1em',
                              color: (() => {
                                const f = parseFloat(formData.locationMinPlotFrontage); const d = parseFloat(formData.locationMinPlotDepth);
                                const minA = f > 0 && d > 0 ? f * d : 620;
                                return parseFloat(formData.landAreaSqMtrs) >= minA ? '#27ae60' : '#e74c3c';
                              })()
                            }}
                          >
                            {(() => {
                              const f = parseFloat(formData.locationMinPlotFrontage); const d = parseFloat(formData.locationMinPlotDepth);
                              const minA = f > 0 && d > 0 ? f * d : 620;
                              return parseFloat(formData.landAreaSqMtrs) >= minA ? '✓' : 'ⓘ';
                            })()}
                          </span>
                        )}
                      </div>
                      {formData.locationMinPlotFrontage && formData.locationMinPlotDepth && (
                        <span style={{ fontSize: '0.85em', color: '#666', whiteSpace: 'nowrap' }}>
                          Min: {(() => { const f = parseFloat(formData.locationMinPlotFrontage); const d = parseFloat(formData.locationMinPlotDepth); return f > 0 && d > 0 ? f * d : 620; })()} Sq. Mtrs
                        </span>
                      )}
                    </div>
                    {formData.landAreaSqMtrs && formData.locationMinPlotFrontage && formData.locationMinPlotDepth && (() => {
                      const f = parseFloat(formData.locationMinPlotFrontage); const d = parseFloat(formData.locationMinPlotDepth);
                      const minA = f > 0 && d > 0 ? f * d : 620;
                      return parseFloat(formData.landAreaSqMtrs) < minA;
                    })() && (
                      <div style={{ color: '#e74c3c', fontSize: '0.85em', marginTop: '5px' }}>
                        {(() => {
                          const f = parseFloat(formData.locationMinPlotFrontage); const d = parseFloat(formData.locationMinPlotDepth);
                          const minA = f > 0 && d > 0 ? f * d : 620;
                          const label = f > 0 && d > 0 ? `${minA} Sq. Mtrs (${formData.locationMinPlotFrontage} × ${formData.locationMinPlotDepth})` : `${minA} Sq. Mtrs (as per Brochure)`;
                          return `⚠️ Total area must be at least ${label}`;
                        })()}
                      </div>
                    )}
                  </td>
                </tr>
                <tr ref={landApproachRoadWidthMtrsRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Approach Road Width (Mtrs)
                    {mandatoryFieldsPage2.landApproachRoadWidthMtrs && !formData.landApproachRoadWidthMtrs && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandApproachRoadWidthMtrsShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="landApproachRoadWidthMtrs" value={formData.landApproachRoadWidthMtrs || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0 }} />
                  </td>
                </tr>
                <tr ref={landPlotLevelTypeRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Plot Level from Road level
                    {mandatoryFieldsPage2.landPlotLevelType && !formData.landPlotLevelType && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandPlotLevelTypeShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <PlotLevelContainer>
                      {/* Above option with its own value box */}
                      <PlotLevelOptionGroup isSelected={formData.landPlotLevelType === 'Above'}>
                        <PlotLevelOption isSelected={formData.landPlotLevelType === 'Above'} hasValueBox={formData.landPlotLevelType === 'Above'}>
                          <input type="radio" name="landPlotLevelType" value="Above" checked={formData.landPlotLevelType === 'Above'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                          Above
                        </PlotLevelOption>
                        {formData.landPlotLevelType === 'Above' && (
                          <PlotLevelValueBox>
                            <input type="text" name="landPlotLevelMtr" value={formData.landPlotLevelMtr || ''} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} placeholder="Value" />
                            <span>Mtr.</span>
                          </PlotLevelValueBox>
                        )}
                      </PlotLevelOptionGroup>
                      
                      {/* Below option with its own value box */}
                      <PlotLevelOptionGroup isSelected={formData.landPlotLevelType === 'Below'}>
                        <PlotLevelOption isSelected={formData.landPlotLevelType === 'Below'} hasValueBox={formData.landPlotLevelType === 'Below'}>
                          <input type="radio" name="landPlotLevelType" value="Below" checked={formData.landPlotLevelType === 'Below'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                          Below
                        </PlotLevelOption>
                        {formData.landPlotLevelType === 'Below' && (
                          <PlotLevelValueBox>
                            <input type="text" name="landPlotLevelMtr" value={formData.landPlotLevelMtr || ''} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} placeholder="Value" />
                            <span>Mtr.</span>
                          </PlotLevelValueBox>
                        )}
                      </PlotLevelOptionGroup>
                    </PlotLevelContainer>
                  </td>
                </tr>
                {/* Detail of surroundings - merged into LAND OWNERSHIP DETAILS */}
                <tr>
                  <td colSpan={2} style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#aed6f1', fontWeight: 700, textAlign: 'center', fontSize: '1em' }}>Surroundings of Offered Land/Plot</td>
                </tr>
                <tr ref={surroundingsEastRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '35%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      East
                      {mandatoryFieldsPage2.surroundingsEast && !formData.surroundingsEast && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                      )}
                      <span title="Describe what is on the East side of the Offered Land/Plot (e.g., Road, Building, Open Land, Residential Area)" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isSurroundingsEastShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="surroundingsEast" value={formData.surroundingsEast || ''} onChange={handleChange} placeholder="e.g., 30ft Road, Residential Building" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%', minWidth: '250px' }} />
                  </td>
                </tr>
                <tr ref={surroundingsWestRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      West
                      {mandatoryFieldsPage2.surroundingsWest && !formData.surroundingsWest && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                      )}
                      <span title="Describe what is on the West side of the Offered Land/Plot (e.g., Road, Building, Open Land, Residential Area)" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isSurroundingsWestShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="surroundingsWest" value={formData.surroundingsWest || ''} onChange={handleChange} placeholder="e.g., Open Land, Commercial Complex" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%', minWidth: '250px' }} />
                  </td>
                </tr>
                <tr ref={surroundingsNorthRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      North
                      {mandatoryFieldsPage2.surroundingsNorth && !formData.surroundingsNorth && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                      )}
                      <span title="Describe what is on the North side of the Offered Land/Plot (e.g., Road, Building, Open Land, Residential Area)" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isSurroundingsNorthShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="surroundingsNorth" value={formData.surroundingsNorth || ''} onChange={handleChange} placeholder="e.g., Agricultural Land, Highway" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%', minWidth: '250px' }} />
                  </td>
                </tr>
                <tr ref={surroundingsSouthRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      South
                      {mandatoryFieldsPage2.surroundingsSouth && !formData.surroundingsSouth && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                      )}
                      <span title="Describe what is on the South side of the Offered Land/Plot (e.g., Road, Building, Open Land, Residential Area)" style={{ cursor: 'help', color: '#3498db' }}>ⓘ</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isSurroundingsSouthShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="surroundingsSouth" value={formData.surroundingsSouth || ''} onChange={handleChange} placeholder="e.g., Petrol Pump, Main Road" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, width: '100%', minWidth: '250px' }} />
                  </td>
                </tr>
                <tr ref={landUseRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Offered Land/Plot Use
                    {mandatoryFieldsPage2.landUse && !formData.landUse && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandUseShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      {/* Agricultural option */}
                      <div 
                        style={{ 
                          padding: '10px 16px',
                          border: `2px solid ${formData.landUse === 'Agricultural' ? '#3498db' : '#ddd'}`,
                          borderRadius: '6px',
                          backgroundColor: formData.landUse === 'Agricultural' ? '#ebf5fb' : '#fff',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: formData.landUse === 'Agricultural' ? 600 : 400 }}>
                          <input type="radio" name="landUse" value="Agricultural" checked={formData.landUse === 'Agricultural'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} style={{ accentColor: '#3498db' }} />
                          Agricultural
                        </label>
                      </div>
                      
                      {/* Non-Agricultural parent with connected children */}
                      <div 
                        style={{ 
                          border: `2px solid ${(formData.landUse === 'Non-Agricultural' || formData.landUse === 'Commercial' || formData.landUse === 'Industrial') ? '#3498db' : '#ddd'}`,
                          borderRadius: '6px',
                          backgroundColor: (formData.landUse === 'Non-Agricultural' || formData.landUse === 'Commercial' || formData.landUse === 'Industrial') ? '#ebf5fb' : '#fff',
                          transition: 'all 0.2s ease',
                          overflow: 'hidden'
                        }}
                      >
                        {/* Parent option */}
                        <label 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            cursor: 'pointer',
                            padding: '10px 16px',
                            fontWeight: (formData.landUse === 'Non-Agricultural' || formData.landUse === 'Commercial' || formData.landUse === 'Industrial') ? 600 : 400,
                            borderBottom: (formData.landUse === 'Non-Agricultural' || formData.landUse === 'Commercial' || formData.landUse === 'Industrial') ? '1px solid #85c1e9' : 'none'
                          }}
                        >
                          <input 
                            type="radio" 
                            name="landUseParent" 
                            checked={formData.landUse === 'Non-Agricultural' || formData.landUse === 'Commercial' || formData.landUse === 'Industrial'} 
                            onChange={() => handleChange({ target: { name: 'landUse', value: 'Non-Agricultural' } } as any)} 
                            disabled={!isOnline || isViewMode || isApplicationLocked} 
                            style={{ accentColor: '#3498db' }} 
                          />
                          Non-Agricultural
                        </label>
                        
                        {/* Child options - visible when Non-Agricultural is selected */}
                        {(formData.landUse === 'Non-Agricultural' || formData.landUse === 'Commercial' || formData.landUse === 'Industrial') && (
                          <div style={{ 
                            display: 'flex', 
                            gap: '16px', 
                            padding: '10px 16px 10px 36px',
                            backgroundColor: '#f0f7fc',
                            animation: 'fadeSlideIn 0.25s ease-out'
                          }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.95em' }}>
                              <input type="radio" name="landUse" value="Commercial" checked={formData.landUse === 'Commercial'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} style={{ accentColor: '#27ae60' }} />
                              Commercial
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.95em' }}>
                              <input type="radio" name="landUse" value="Industrial" checked={formData.landUse === 'Industrial'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} style={{ accentColor: '#27ae60' }} />
                              Industrial
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr ref={landTitleClearRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Whether title of plot is clear, Marketable and undisputed If No Specify in details
                    {mandatoryFieldsPage2.landTitleClear && !formData.landTitleClear && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandTitleClearShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: formData.landTitleClear === 'No' ? '10px' : '0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landTitleClear" value="Yes" checked={formData.landTitleClear === 'Yes'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landTitleClear" value="No" checked={formData.landTitleClear === 'No'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        No
                      </label>
                    </div>
                    {formData.landTitleClear === 'No' && (
                      <StyledTextArea name="landTitleClearDetails" value={formData.landTitleClearDetails || ''} onChange={handleChange} placeholder="Specify details" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, minHeight: '50px', borderColor: !formData.landTitleClearDetails?.trim() ? '#dc2626' : undefined }} />
                    )}
                    {formData.landTitleClear === 'No' && !formData.landTitleClearDetails?.trim() && (
                      <div style={{ color: '#dc2626', fontSize: '0.75em', marginTop: '4px' }}>⚠️ Details required when "No" is selected</div>
                    )}
                  </td>
                </tr>
                <tr ref={landLitigationFreeRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Litigation Free if No specify in details
                    {mandatoryFieldsPage2.landLitigationFree && !formData.landLitigationFree && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandLitigationFreeShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: formData.landLitigationFree === 'No' ? '10px' : '0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landLitigationFree" value="Yes" checked={formData.landLitigationFree === 'Yes'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landLitigationFree" value="No" checked={formData.landLitigationFree === 'No'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        No
                      </label>
                    </div>
                    {formData.landLitigationFree === 'No' && (
                      <StyledTextArea name="landLitigationFreeDetails" value={formData.landLitigationFreeDetails || ''} onChange={handleChange} placeholder="Specify details" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, minHeight: '50px', borderColor: !formData.landLitigationFreeDetails?.trim() ? '#dc2626' : undefined }} />
                    )}
                    {formData.landLitigationFree === 'No' && !formData.landLitigationFreeDetails?.trim() && (
                      <div style={{ color: '#dc2626', fontSize: '0.75em', marginTop: '4px' }}>⚠️ Details required when "No" is selected</div>
                    )}
                  </td>
                </tr>
                <tr ref={landPowerConnectionAvailableRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Nearby Power Connection Available
                    {mandatoryFieldsPage2.landPowerConnectionAvailable && !formData.landPowerConnectionAvailable && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandPowerConnectionAvailableShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landPowerConnectionAvailable" value="Yes" checked={formData.landPowerConnectionAvailable === 'Yes'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landPowerConnectionAvailable" value="No" checked={formData.landPowerConnectionAvailable === 'No'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        No
                      </label>
                    </div>
                  </td>
                </tr>
                <tr ref={landWaterConnectionAvailableRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Water Connection Available
                    {mandatoryFieldsPage2.landWaterConnectionAvailable && !formData.landWaterConnectionAvailable && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandWaterConnectionAvailableShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landWaterConnectionAvailable" value="Yes" checked={formData.landWaterConnectionAvailable === 'Yes'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landWaterConnectionAvailable" value="No" checked={formData.landWaterConnectionAvailable === 'No'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        No
                      </label>
                    </div>
                  </td>
                </tr>
                <tr ref={landEncroachmentRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Encroachment if Yes specify in details
                    {mandatoryFieldsPage2.landEncroachment && !formData.landEncroachment && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandEncroachmentShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: formData.landEncroachment === 'Yes' ? '10px' : '0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landEncroachment" value="Yes" checked={formData.landEncroachment === 'Yes'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landEncroachment" value="No" checked={formData.landEncroachment === 'No'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        No
                      </label>
                    </div>
                    {formData.landEncroachment === 'Yes' && (
                      <StyledTextArea name="landEncroachmentDetails" value={formData.landEncroachmentDetails || ''} onChange={handleChange} placeholder="Specify details" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, minHeight: '50px', borderColor: !formData.landEncroachmentDetails?.trim() ? '#dc2626' : undefined }} />
                    )}
                    {formData.landEncroachment === 'Yes' && !formData.landEncroachmentDetails?.trim() && (
                      <div style={{ color: '#dc2626', fontSize: '0.75em', marginTop: '4px' }}>⚠️ Details required when "Yes" is selected</div>
                    )}
                  </td>
                </tr>
                <tr ref={landGovtAcquisitionPlanRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Whether Offered Land/Plot is at any time covered in Govt. Acquisition Plan If Yes Give the details
                    {mandatoryFieldsPage2.landGovtAcquisitionPlan && !formData.landGovtAcquisitionPlan && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandGovtAcquisitionPlanShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: formData.landGovtAcquisitionPlan === 'Yes' ? '10px' : '0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landGovtAcquisitionPlan" value="Yes" checked={formData.landGovtAcquisitionPlan === 'Yes'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landGovtAcquisitionPlan" value="No" checked={formData.landGovtAcquisitionPlan === 'No'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        No
                      </label>
                    </div>
                    {formData.landGovtAcquisitionPlan === 'Yes' && (
                      <StyledTextArea name="landGovtAcquisitionPlanDetails" value={formData.landGovtAcquisitionPlanDetails || ''} onChange={handleChange} placeholder="Give the details" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, minHeight: '50px', borderColor: !formData.landGovtAcquisitionPlanDetails?.trim() ? '#dc2626' : undefined }} />
                    )}
                    {formData.landGovtAcquisitionPlan === 'Yes' && !formData.landGovtAcquisitionPlanDetails?.trim() && (
                      <div style={{ color: '#dc2626', fontSize: '0.75em', marginTop: '4px' }}>⚠️ Details required when "Yes" is selected</div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </FormFieldFullWidth>

          {/* OTHER INFORMATION Section */}
          <FormFieldFullWidth style={{ marginTop: '25px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <Label style={{ fontWeight: 700, marginBottom: '20px', color: '#1a5276', display: 'block', fontSize: '1.05em', textAlign: 'center', textDecoration: 'underline' }}>OTHER INFORMATION (OFFERED LAND/PLOT RELATED)</Label>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr ref={nearestPoliceStationNameRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600, width: '60%' }}>
                    Nearest Police Station Name & Distance (in meters) from the Offered Land/Plot
                    {mandatoryFieldsPage2.nearestPoliceStationName && !formData.nearestPoliceStationName && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isNearestPoliceStationNameShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="nearestPoliceStationName" value={formData.nearestPoliceStationName || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0 }} />
                  </td>
                </tr>
                <tr ref={nearestFireStationNameRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Nearest Fire Station Name & Distance (in meters) from the Offered Land/Plot
                    {mandatoryFieldsPage2.nearestFireStationName && !formData.nearestFireStationName && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isNearestFireStationNameShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <StyledInput type="text" name="nearestFireStationName" value={formData.nearestFireStationName || ''} onChange={handleChange} placeholder="To be filled by applicant" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0 }} />
                  </td>
                </tr>
                <tr ref={highVoltageTransmissionLineRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Any High-Voltage/Low-Voltage Transmission line passing through the Offered Land/Plot If Yes then Distance in Meter
                    {mandatoryFieldsPage2.highVoltageTransmissionLine && !formData.highVoltageTransmissionLine && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isHighVoltageTransmissionLineShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: formData.highVoltageTransmissionLine === 'Yes' ? '10px' : '0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="highVoltageTransmissionLine" value="Yes" checked={formData.highVoltageTransmissionLine === 'Yes'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="highVoltageTransmissionLine" value="No" checked={formData.highVoltageTransmissionLine === 'No'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        No
                      </label>
                    </div>
                    {formData.highVoltageTransmissionLine === 'Yes' && (
                      <StyledInput type="text" name="highVoltageTransmissionLineDistance" value={formData.highVoltageTransmissionLineDistance || ''} onChange={handleChange} placeholder="Distance in Meter" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, borderColor: !formData.highVoltageTransmissionLineDistance?.trim() ? '#dc2626' : undefined }} />
                    )}
                    {formData.highVoltageTransmissionLine === 'Yes' && !formData.highVoltageTransmissionLineDistance?.trim() && (
                      <div style={{ color: '#dc2626', fontSize: '0.75em', marginTop: '4px' }}>⚠️ Distance required when "Yes" is selected</div>
                    )}
                  </td>
                </tr>
                <tr ref={landAbuttingRoadBoundaryRef}>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', backgroundColor: '#d6eaf8', fontWeight: 600 }}>
                    Does the land touch the road after the official road boundary (Right of Way)? If yes, how far is it from the road (in meters)?
                    {mandatoryFieldsPage2.landAbuttingRoadBoundary && !formData.landAbuttingRoadBoundary && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '10px', fontSize: '0.75em', fontWeight: 600 }}>Required</span>
                    )}
                  </td>
                  <td style={{ padding: '12px', border: '1px solid #85c1e9', animation: isLandAbuttingRoadBoundaryShaking ? 'shake 0.5s ease-in-out' : 'none' }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: formData.landAbuttingRoadBoundary === 'Yes' ? '10px' : '0' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landAbuttingRoadBoundary" value="Yes" checked={formData.landAbuttingRoadBoundary === 'Yes'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input type="radio" name="landAbuttingRoadBoundary" value="No" checked={formData.landAbuttingRoadBoundary === 'No'} onChange={handleChange} disabled={!isOnline || isViewMode || isApplicationLocked} />
                        No
                      </label>
                    </div>
                    {formData.landAbuttingRoadBoundary === 'Yes' && (
                      <StyledInput type="text" name="landAbuttingRoadBoundaryDistance" value={formData.landAbuttingRoadBoundaryDistance || ''} onChange={handleChange} placeholder="Distance in Meter" disabled={!isOnline || isViewMode || isApplicationLocked} style={{ margin: 0, borderColor: !formData.landAbuttingRoadBoundaryDistance?.trim() ? '#dc2626' : undefined }} />
                    )}
                    {formData.landAbuttingRoadBoundary === 'Yes' && !formData.landAbuttingRoadBoundaryDistance?.trim() && (
                      <div style={{ color: '#dc2626', fontSize: '0.75em', marginTop: '4px' }}>⚠️ Distance required when "Yes" is selected</div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </FormFieldFullWidth>

        </FormContentContainer>
        <PageNumber>2</PageNumber>
        {/* <PayNowButton onClick={handlePayment}>Pay Application Fee</PayNowButton> */}
      </A4PageContainer>
      <FilePreviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        fileUrl={modalFileUrl}
        fileName={modalFileName}
        fileType={modalFileType}
        applicationId={applicationId}
        fieldName={modalFieldName || undefined}
        onSaveSuccess={(newFilePath) => {
          if (modalFieldName) {
            setFormData(prev => ({ ...prev, [modalFieldName]: newFilePath }));
            saveToDatabaseCallback({ ...formData, [modalFieldName]: newFilePath }, true);
          }
        }}
      />
      
      {isPdfGenerating && (
        <ModalOverlay>
          <ModalContent>
            <h3>Generating PDF</h3>
            <p>Please wait... Generating PDF</p>
            <ProgressBarContainerModal>
              <ProgressBarFillModal progress={compressionProgress} />
            </ProgressBarContainerModal>
            <ProgressText>Compression: {compressionProgress}%</ProgressText>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageWrapper>

    {/* Lease Agreement Modal - Outside PageWrapper for proper fixed positioning */}
    {showLeaseModal && (
      <ModalOverlay>
        <ModalContent style={{ maxWidth: '500px', textAlign: 'center' }}>
          <h3 style={{ color: '#1a5276', marginBottom: '20px' }}>Lease Agreement Notice</h3>
          <p style={{ fontSize: '1.05em', lineHeight: '1.6', marginBottom: '25px', color: '#333' }}>
            By virtue of Lease/Sub-Lease, the Applicant will transfer the leasehold rights in the offered land in favour of MNGL
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
            <button
              onClick={() => {
                setLeaseAgreed(true);
                setShowLeaseModal(false);
                handleChange({ target: { name: 'landOwnershipStatus', value: 'Lease' } } as any);
              }}
              style={{
                padding: '12px 30px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1em',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              I Agree
            </button>
            <button
              onClick={() => {
                setLeaseAgreed(false);
                setShowLeaseModal(false);
              }}
              style={{
                padding: '12px 30px',
                backgroundColor: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1em',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              I Don't Agree
            </button>
          </div>
        </ModalContent>
      </ModalOverlay>
    )}

    {/* Upload Error Modal - Outside PageWrapper for proper fixed positioning */}
    {uploadErrorModal.show && (
      <ModalOverlay onClick={() => setUploadErrorModal({ show: false, message: '', fieldName: '' })}>
        <ModalContent 
          style={{ maxWidth: '400px', textAlign: 'center', padding: '25px 30px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: '3em', marginBottom: '15px' }}>😕</div>
          <h3 style={{ color: '#e74c3c', marginBottom: '15px', fontSize: '1.2em' }}>Upload Failed</h3>
          <p style={{ fontSize: '0.95em', lineHeight: '1.6', marginBottom: '20px', color: '#555' }}>
            {uploadErrorModal.message}
          </p>
          <div style={{ fontSize: '0.85em', color: '#888', marginBottom: '20px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
            <strong>Tips:</strong>
            <ul style={{ textAlign: 'left', margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Check your internet connection</li>
              <li>Ensure file is PDF, JPEG, or PNG</li>
              <li>File size should be under 10MB</li>
              <li>Try a different file if issue persists</li>
            </ul>
          </div>
          <button
            onClick={() => setUploadErrorModal({ show: false, message: '', fieldName: '' })}
            style={{
              padding: '10px 30px',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1em',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Got It
          </button>
        </ModalContent>
      </ModalOverlay>
    )}

    {/* Coordinates Help Modal - Outside PageWrapper for proper fixed positioning */}
    {showCoordinatesHelpModal && (
      <ModalOverlay onClick={() => setShowCoordinatesHelpModal(false)}>
        <ModalContent 
          style={{ maxWidth: '950px', width: '95vw', textAlign: 'left', padding: '15px 20px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ color: '#1a5276', margin: 0, display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.1em' }}>
              📍 How to Find Land/Plot Coordinates
            </h3>
            <button
              onClick={() => setShowCoordinatesHelpModal(false)}
              style={{ background: 'none', border: 'none', fontSize: '1.4em', cursor: 'pointer', color: '#666', padding: '0 5px', lineHeight: 1 }}
            >
              ×
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '15px' }}>
            {/* Left Section - Text Instructions */}
            <div style={{ flex: '1', minWidth: 0 }}>
              <div style={{ backgroundColor: '#e8f4fd', padding: '10px', borderRadius: '6px', marginBottom: '8px' }}>
                <h4 style={{ color: '#2980b9', margin: '0 0 6px 0', fontSize: '0.9em' }}>🖥️ On Computer:</h4>
                <ol style={{ margin: 0, paddingLeft: '16px', lineHeight: '1.4', color: '#333', fontSize: '0.8em' }}>
                  <li>Open <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" style={{ color: '#3498db' }}>Google Maps</a></li>
                  <li>Navigate to your land/plot location</li>
                  <li><strong>Right-click</strong> on the exact location</li>
                  <li>Click coordinates (e.g., <code style={{ backgroundColor: '#fff', padding: '1px 3px', borderRadius: '2px', color: '#e74c3c', fontSize: '0.85em' }}>19.0760, 72.8777</code>)</li>
                  <li>Coordinates are <strong>auto-copied</strong> to clipboard</li>
                  <li><strong>Paste</strong> in Land/Plot Location field</li>
                </ol>
              </div>
              
              <div style={{ backgroundColor: '#fef9e7', padding: '10px', borderRadius: '6px', marginBottom: '8px' }}>
                <h4 style={{ color: '#9a7b0a', margin: '0 0 6px 0', fontSize: '0.9em' }}>📱 On Mobile:</h4>
                <ol style={{ margin: 0, paddingLeft: '16px', lineHeight: '1.4', color: '#333', fontSize: '0.8em' }}>
                  <li>Open Google Maps app</li>
                  <li><strong>Long press</strong> on your location</li>
                  <li>Red pin appears with coordinates at bottom</li>
                  <li><strong>Tap</strong> coordinates to copy</li>
                  <li><strong>Paste</strong> in the field</li>
                </ol>
              </div>
              
              <div style={{ backgroundColor: '#e8f8f5', padding: '10px', borderRadius: '6px' }}>
                <h4 style={{ color: '#1e8449', margin: '0 0 6px 0', fontSize: '0.9em' }}>🔗 Alternative:</h4>
                <p style={{ margin: 0, lineHeight: '1.4', color: '#333', fontSize: '0.8em' }}>
                  Paste the <strong>Google Maps URL</strong> of your location. Click "Share" on Google Maps and copy the link!
                </p>
              </div>
            </div>
            
            {/* Right Section - Video */}
            <div style={{ flex: '1.2', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ backgroundColor: '#fdf2f8', padding: '10px', borderRadius: '6px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h4 style={{ color: '#be185d', margin: '0 0 8px 0', fontSize: '0.9em' }}>🎬 Video Tutorial:</h4>
                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '6px', flex: 1 }}>
                  <iframe
                    src="https://www.youtube.com/embed/eD7UD-PnPRs"
                    title="How to find coordinates on Google Maps"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center', marginTop: '12px' }}>
            <button
              onClick={() => setShowCoordinatesHelpModal(false)}
              style={{
                padding: '8px 30px',
                background: 'linear-gradient(135deg, #3498db, #2980b9)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.9em',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 3px 10px rgba(52, 152, 219, 0.3)'
              }}
            >
              Got it!
            </button>
          </div>
        </ModalContent>
      </ModalOverlay>
    )}
    
    {/* Empty ActionButtonsContainer to maintain structure */}
    <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000 }}>
      {/* Next Page button moved near FormPageNavigator */}
    </ActionButtonsContainer>
    
    {/* Session Timer */}
    {!isViewMode && (
      <SessionTimer 
        sessionDuration={30}
        warningTime={5}
        onSessionExpired={() => navigate('/')}
        onSaveState={async () => {
          if (applicationId) {
            await saveToDatabaseCallback(formData, true, applicationId);
          }
        }}
      />
    )}

    {/* Application ID Display */}
    {applicationId && <ApplicationIdDisplay applicationId={applicationId} />}

    {/* Home Icon Button - Above Save Icon */}
    <button
      onClick={() => {
        // Skip save modal if application is locked
        if (applicationId && formData.emailId && formData.emailId.trim() !== '' && isOnline && !isApplicationLocked) {
          setPendingNavigation('/user-dashboard');
          setShowSaveModal(true);
        } else {
          navigate('/user-dashboard');
        }
      }}
      title="Go to Dashboard"
      style={{
        position: 'fixed',
        left: '20px',
        bottom: '150px',
        zIndex: 1000,
        height: '44px',
        padding: '0 16px 0 12px',
        borderRadius: '22px',
        border: 'none',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: '14px',
        fontWeight: 600,
        boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
        transition: 'all 0.25s ease'
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
      Home
    </button>

    {/* Save Icon Button - Above Back Button - Hidden when application is locked */}
    {!isViewMode && !isApplicationLocked && (
      <button
        onClick={() => saveToDatabaseCallback(formData, true, applicationId)}
        disabled={isSaving || !applicationId || !isOnline}
        title="Save Draft"
        style={{
          position: 'fixed',
          left: '20px',
          bottom: '98px',
          zIndex: 1000,
          height: '44px',
          padding: '0 16px 0 12px',
          borderRadius: '22px',
          border: 'none',
          background: isSaving ? '#86efac' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          color: 'white',
          cursor: isSaving || !applicationId || !isOnline ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 14px rgba(34, 197, 94, 0.35)',
          transition: 'all 0.25s ease',
          opacity: isSaving || !applicationId || !isOnline ? 0.6 : 1
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
          <polyline points="17 21 17 13 7 13 7 21"/>
          <polyline points="7 3 7 8 15 8"/>
        </svg>
        {isSaving ? 'Saving...' : 'Save'}
      </button>
    )}

    {/* Save Progress Modal */}
    {showSaveModal && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2200,
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '28px 32px',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          maxWidth: '380px',
          width: '90%',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2.5em', marginBottom: '12px' }}>💾</div>
          <h3 style={{ margin: '0 0 10px 0', color: '#2c3e50', fontSize: '1.15em', fontWeight: 600 }}>
            Save your progress?
          </h3>
          <p style={{ margin: '0 0 20px 0', fontSize: '0.9em', color: '#666', lineHeight: 1.5 }}>
            Would you like to save your form before continuing?
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={handleSkipSaveAndNavigate}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f1f1f1',
                color: '#555',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9em',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Skip
            </button>
            <button
              type="button"
              onClick={handleSaveAndNavigate}
              style={{
                padding: '10px 24px',
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.9em',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Save & Continue
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Minimum Plot Size Requirements Modal */}
    {showPlotSizeModal && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2100,
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          maxWidth: '700px',
          width: '95%',
        }}>
          <h3 style={{ margin: '0 0 16px 0', color: '#2c3e50', fontSize: '1.1em', textAlign: 'center' }}>
            Minimum Plot Size Requirements
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '0.9em', color: '#555' }}>
            Minimum Plot size of offered land should be as per following table:
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85em' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'left', fontWeight: 600 }}>Location</th>
                <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center', fontWeight: 600 }}>Frontage in Meter (feet)</th>
                <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center', fontWeight: 600 }}>Depth in Meter (feet)</th>
                <th style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center', fontWeight: 600 }}>Total Area in Sq. Meter (Sq. Feet)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #ddd', padding: '10px' }}>Within city</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>20 (66)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>31 (102)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>620 (6732)</td>
              </tr>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td style={{ border: '1px solid #ddd', padding: '10px' }}>Other road other than NH</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>31 (102)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>31 (102)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>961 (10404)</td>
              </tr>
              <tr>
                <td style={{ border: '1px solid #ddd', padding: '10px' }}>NH (Undivided)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>35 (115)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>35 (115)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>1225 (13225)</td>
              </tr>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td style={{ border: '1px solid #ddd', padding: '10px' }}>NH (Divided)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>35 (115)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>45 (148)</td>
                <td style={{ border: '1px solid #ddd', padding: '10px', textAlign: 'center' }}>1575 (17020)</td>
              </tr>
            </tbody>
          </table>
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowPlotSizeModal(false)}
              style={{
                padding: '10px 32px',
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1em',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Brochure PDF Modal */}
    {showBrochureModal && (
      <ModalOverlay onClick={() => setShowBrochureModal(false)}>
        <div 
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90vw',
            maxWidth: '900px',
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f8fafc'
          }}>
            <h3 style={{ margin: 0, color: '#1e40af', fontWeight: 600 }}>📄 MNGL DODO Brochure</h3>
            <button
              onClick={() => setShowBrochureModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '4px 8px',
                borderRadius: '4px'
              }}
            >
              ✕
            </button>
          </div>
          <iframe
            src="/brochures/mngl-dodo-brochure.pdf"
            style={{
              flex: 1,
              width: '100%',
              border: 'none'
            }}
            title="MNGL DODO Brochure"
          />
        </div>
      </ModalOverlay>
    )}
    </>
  );
};

export default ApplicationFormPage2;

// Helper to make all props of a styled component optional for the 'as' prop or other scenarios
type Optional<T> = { [P in keyof T]+?: T[P] };

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
`;

const ProgressBarContainerModal = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  margin-top: 5px;
  overflow: hidden;
`;

const ProgressBarFillModal = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.p`
  font-size: 0.8em;
  margin-top: 5px;
  text-align: center;
`;
